// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Three routes for one subject: the list, one invoice, and the form that writes
// a draft. The detail page is not a modal because it is a place people link
// each other to and keep open in a tab while they are on the phone about a
// payment — and because it is the page somebody prints from.

export const routes = [
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('./pages/InvoicesPage.vue'),
    meta: { permission: 'invoicing:read', title: 'invoicing.title' , ownsQuery: true /* limpia la consulta después de leerla: el armazón no la remonta al cambiar */ },
  },
  // The static paths sit first for the reader; route ranking would win anyway.
  //
  // The editor covers the shell (`position: fixed`) rather than sitting inside
  // it: writing an invoice is eleven columns of lines with their breakdown
  // underneath and a catalogue to pull from, and none of that fits next to a
  // sidebar. It is still a route and not a modal, so a half-written invoice can
  // be linked to, reloaded and come back from the browser's back button.
  {
    path: '/invoices/new',
    name: 'invoice-new',
    component: () => import('./pages/InvoiceFormPage.vue'),
    meta: { permission: 'invoicing:write', title: 'invoicing.form.newTitle', fullScreen: true },
  },
  // The chain, its queue and the button that walks it. Behind `manage` because
  // everything on it is an administrator's question, and reached from the list
  // rather than from the sidebar: a screen that is blank on every instance
  // where Veri*Factu is off has no business taking one of the few nav slots the
  // next module installed has the same claim to.
  {
    path: '/invoices/verifactu',
    name: 'invoice-verifactu',
    component: () => import('./pages/VerifactuPage.vue'),
    meta: { permission: 'invoicing:manage', title: 'invoicing.verifactu.title' },
  },
  {
    path: '/invoices/:id',
    name: 'invoice',
    component: () => import('./pages/InvoicePage.vue'),
    meta: { permission: 'invoicing:read', title: 'invoicing.invoice.title' },
  },
  // Reachable only for a draft. An issued invoice that lands here is sent
  // straight back to its own page by the form — the API would refuse the save
  // anyway, but being refused after typing for ten minutes is not an answer.
  {
    path: '/invoices/:id/edit',
    name: 'invoice-edit',
    component: () => import('./pages/InvoiceFormPage.vue'),
    meta: { permission: 'invoicing:write', title: 'invoicing.form.editTitle', fullScreen: true },
  },
]

// Only the list. Everything else is reached from it, from the search box or
// from a link — a second sidebar entry would take one of the few slots the next
// module installed has the same claim to.
export const navigation = [
  { to: '/invoices', labelKey: 'invoicing.title', permission: 'invoicing:read', icon: 'receipt', group: 'business' },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
//
// `pdf` is the odd branch. Those words are not shown on any screen — they are
// sent to the server with the print request and end up inside the document.
// They live here because there must be exactly one place per module where a
// sentence is translated, and the backend has no i18n at all.
const baseMessages = {
  es: {
    invoicing: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'una factura vencida | {count} facturas vencidas',
      },
      title: 'Facturas',
      dashboard: {
        outstanding: 'Pendiente de cobro',
        outstandingHelp: 'Suma de lo emitido y todavía no cobrado, en euros, contando los cobros parciales.',
        overdue: 'Facturas vencidas',
        overdueHelp: 'Cuántas facturas emitidas han pasado de su fecha de vencimiento sin cobrarse del todo.',
        invoicedMonth: 'Facturado este mes',
        invoicedMonthHelp: 'Total emitido desde el día 1 del mes en curso, impuestos incluidos.',
        collectedMonth: 'Cobrado este mes',
        collectedMonthHelp: 'Lo que ha entrado desde el día 1 del mes en curso, contra facturas emitidas.',
        unpaid: 'Por cobrar',
        unpaidHelp: 'Las facturas sin cobrar cuyo vencimiento llega antes, de la más urgente a la menos. Estira la tarjeta y caben más.',
      },
      subtitle: 'Lo que habéis facturado, lo que os han pagado y lo que falta por cobrar',
      actions: {
        new: 'Nueva factura', edit: 'Editar', delete: 'Eliminar borrador',
        deleteConfirm: 'Se borra del todo. No tiene número ni lo ha visto nadie, así que no se pierde nada más.',
        issue: 'Emitir', cancel: 'Anular', rectify: 'Rectificar',
        payment: 'Registrar cobro', send: 'Enviar', resend: 'Reenviar', print: 'Descargar PDF',
        save: 'Guardar', close: 'Cerrar', back: 'Volver', confirm: 'Confirmar', cancelAction: 'Cancelar',
      },
      filters: {
        oneCustomer: 'Solo de un cliente',

        search: 'Buscar', searchPlaceholder: 'Número, cliente, concepto…',
        status: 'Estado', allStatuses: 'Todos',
        dateFrom: 'Desde', dateTo: 'Hasta',
      },
      // Dos de estos seis no se guardan en ninguna parte: «vencida» y «cobrada
      // en parte» se calculan al leer, y por eso no hay ningún proceso nocturno
      // que tenga que mantenerlos al día.
      status: {
        draft: 'Borrador', issued: 'Emitida', partially_paid: 'Cobrada en parte',
        overdue: 'Vencida', paid: 'Cobrada', rectified: 'Rectificada', cancelled: 'Anulada',
      },
      series: { invoice: 'Factura', rectificative: 'Factura rectificativa' },
      table: {
        number: 'Número', customer: 'Cliente', issueDate: 'Fecha', dueDate: 'Vencimiento',
        total: 'Total', outstanding: 'Pendiente', status: 'Estado',
      },
      kpi: {
        invoiced: 'Facturado', collected: 'Cobrado',
        outstanding: 'Pendiente de cobro', overdue: 'Vencido',
        nextNumber: 'Siguiente número',
      },
      empty: {
        none: 'Todavía no hay facturas',
        noneHint: 'Escribe la primera. Si tenéis presupuestos u órdenes de trabajo, también pueden abrir el borrador solas.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      list: {
        count: '{count} facturas', view: 'Ver detalle',
        paid: 'Cobrado', paidShort: 'cobrado {amount}',
      },

      form: {
        newTitle: 'Nueva factura', editTitle: 'Editar borrador',
        saveDraft: 'Guardar borrador', saving: 'Guardando…', issuing: 'Emitiendo…',
        saveAndIssue: 'Guardar y emitir',
        subtitle: 'Escribe lo que se cobra, y arrastra del catálogo lo que haga falta',
        sectionInfo: 'La factura', sectionNotes: 'Notas',
        customer: 'Cliente',
        customerTaxId: 'CIF/NIF', customerAddress: 'Dirección',
        customerEmail: 'Correo para enviarla',
        project: 'Obra o proyecto',
        title: 'Concepto', titlePlaceholder: 'Revisión anual, obra de la nave 2…',
        issueDate: 'Fecha de factura', dueDate: 'Vencimiento',
        dueHelp: 'Se calcula desde la fecha de la factura, no desde hoy.',
        issueHelp: 'Si no la cambias, la factura toma la fecha del día en que se emita.',
        paymentTerms: 'Forma de pago', paymentMethod: 'Medio de pago',
        retentionRate: 'Retención (IRPF)',
        retentionHelp: 'Se descuenta de la base imponible, no del total con IVA.',
        notes: 'Notas para el cliente', internalNotes: 'Notas internas',
        internalHelp: 'No salen en el PDF ni en el correo.',
        noteEditor: {
          open: 'Abrir en grande',
          done: 'Listo',
          improve: 'Mejorar con IA', improving: 'Escribiendo…',
          improveHint: 'Arregla la redacción sin cambiar lo que dice, y no añade ninguna cifra.',
          undo: 'Deshacer',
          printedHint: 'Sale impresa al pie de la factura.',
          printedPlaceholder: 'Lo que quieras que lea quien recibe la factura…',
          internalPlaceholder: 'Para vosotros: qué se acordó, con quién, qué falta…',
        },
        lines: {
          title: 'Líneas', add: 'Añadir línea',
          description: 'Concepto', detail: 'Detalle', quantity: 'Cantidad', unit: 'Unidad',
          unitPrice: 'Precio', costPrice: 'Coste', margin: 'Margen', marginShort: 'Mrg.',
          discount: 'Dto. %', taxRate: 'IVA %', amount: 'Importe',
          remove: 'Quitar línea', empty: 'Todavía no hay líneas',
          descriptionPlaceholder: 'Título del concepto…',
          detailPlaceholder: 'Descripción de la línea (opcional)…',
          unitPlaceholder: 'ud., h, m…',
          editMargin: 'Escribe el margen y se calcula el coste',
          noCost: 'Sin coste no hay margen que calcular',
          dragToReorder: 'Arrastra para cambiar el orden',
          dragHint: 'Suelta aquí algo del catálogo para añadirlo como línea',
          components: '{count} componentes',
          breakdown: 'Desglose · {count} componentes',
          component: 'Componente', componentsTotal: 'Suma de componentes',
        },
        totals: {
          subtotal: 'Base imponible', discount: 'Descuentos', tax: 'IVA',
          retention: 'Retención', total: 'Total',
          margin: 'Margen medio', globalMargin: 'Margen para todas', applyAll: 'Aplicar',
          marginRequired: 'Escribe un porcentaje antes de aplicarlo',
          noPricedLines: 'No hay ninguna línea con precio a la que aplicárselo',
          confirmGlobalMargin: 'Se pondrá un margen del {percent}% en {count} líneas, y se sobrescribirá el coste que ya tuvieran. ¿Seguimos?',
        },
        summary: {
          title: 'Resumen', customer: 'Cliente', invoice: 'La factura',
          customerEmpty: 'Elige un cliente y sus datos se rellenan solos',
          filled: 'Rellenado desde la ficha del cliente',
          open: 'Abrir el resumen', close: 'Plegar el resumen',
          linesCount: 'líneas', priced: 'con precio',
        },
        nextNumber: 'Al emitirla le tocará el {number}',
        picker: {
          open: 'Abrir el catálogo',
          label: 'Catálogo',
          rates: 'Tarifas',
          close: 'Plegar el catálogo',
          allCategories: 'Todas las categorías',
          dragHint: 'Arrastra {name} a la factura, o haz clic para añadirlo',
          noResults: 'Nada coincide con la búsqueda',
          uncategorised: 'Sin categoría',
          materials: 'Materiales', search: 'Buscar en el catálogo…',
          empty: 'Aquí saldrán los materiales y las tarifas del almacén o de presupuestos',
          loading: 'Cargando el catálogo…',
        },
      },

      invoice: {
        title: 'Factura',
        detailsTitle: 'Detalles de la factura', economicTitle: 'Resumen económico',
        linesTitle: 'Líneas de factura', collected: 'Cobrado',
        outstandingLabel: 'Pendiente', collectedPct: '{pct}% cobrado',
        rectifiedLabel: 'Rectificado ({numbers})',
        draftNotice: 'Es un borrador: todavía no tiene número y se puede cambiar entero.',
        issuedNotice: 'Emitida el {date}. Una factura emitida ya no se toca: si hay que corregirla, se rectifica.',
        importedNotice: 'Emitida el {date} con otro programa e importada aquí. No forma parte del registro Veri*Factu: no se emitió desde este ERP.',
        cancelledNotice: 'Anulada el {date}. El número sigue gastado, que es lo correcto.',
        issuedBy: 'Emitida por', createdBy: 'Creada por',
        cancelReason: 'Motivo de la anulación',
        rectifies: 'Rectifica a la',
        source: {
          label: 'Viene de',
          quote: 'Presupuesto {code}', work_order: 'Orden de trabajo {code}', manual: 'Escrita a mano', import: 'Importada de {code}', importUnnamed: 'Importada de otro programa',
        },
        payments: {
          title: 'Cobros', none: 'Todavía no se ha cobrado nada',
          amount: 'Importe', method: 'Medio', reference: 'Referencia',
          date: 'Fecha', registeredBy: 'Anotado por', remove: 'Quitar este cobro',
          removeConfirm: '¿Quitar este cobro de {amount}? Queda en el registro de auditoría.',
        },
        sent: { never: 'Todavía no se ha enviado', last: 'Enviada por última vez el {date}', count: '{count} envíos' },
      },

      issueDialog: {
        title: 'Emitir la factura',
        question: 'Le tocará el número {number} y se avisará al resto del ERP.',
        warning: 'A partir de ahí no se puede editar. Un error se corrige con una rectificativa.',
      },
      cancelDialog: {
        title: 'Anular la factura',
        question: 'La factura se queda, marcada como anulada, y su número sigue gastado.',
        reason: 'Motivo', reasonHint: 'Lo leerá alguien que no estaba aquí, dentro de tres años.',
      },
      rectifyDialog: {
        title: 'Rectificar la factura',
        explain: 'Se abre un borrador nuevo con las líneas en negativo. Lo revisas y lo emites tú.',
        reason: 'Qué se está corrigiendo',
      },
      payment: {
        title: 'Registrar un cobro',
        amount: 'Importe', outstanding: 'Queda por cobrar {amount}',
        method: 'Medio de pago', reference: 'Referencia', date: 'Fecha del cobro',
        notes: 'Notas', settles: 'Con esto queda cobrada del todo',
        totalInvoice: 'Total factura', alreadyPaid: 'Ya cobrado', pending: 'Pendiente',
        saving: 'Guardando…', confirm: 'Confirmar cobro',
        methods: {
          transfer: 'Transferencia', card: 'Tarjeta', cash: 'Efectivo',
          direct_debit: 'Domiciliación', cheque: 'Cheque', other: 'Otro',
        },
      },
      send: {
        title: 'Enviar la factura',
        to: 'Para', subject: 'Asunto', body: 'Mensaje',
        subjectDefault: 'Factura {number}',
        draftWithAi: '✦ Redactar', drafting: 'Escribiendo…',
        aiHint: 'La IA escribe el mensaje. Los importes salen de la factura, no de ella.',
        attachment: 'Va adjunta en PDF',
        confirm: 'Enviar', sent: 'Factura enviada',
        noAddress: 'No hay ninguna dirección a la que enviarla',
        disabled: 'Esta instancia todavía no tiene el correo configurado. Descarga el PDF y mándalo tú.',
        templates: 'Plantilla', templateStandard: 'Estándar', templateFormal: 'Formal', templateBrief: 'Breve',
        language: 'Idioma', improve: 'Mejorar con IA', improving: 'Generando…',
        signature: 'Firma automática', signatureEdit: 'Editar', signatureSave: 'Guardar', signatureReset: 'Restablecer',
        toHint: 'Pulsa Intro o coma para añadir otra dirección',
        subjectPlaceholder: 'Asunto del email…', bodyPlaceholder: 'Escribe el mensaje para el cliente…',
        sending: 'Enviando…',
        tplStandardBody: 'Estimado/a cliente:\n\nLe adjuntamos la factura {number} correspondiente a los servicios prestados.\n\nEl importe total asciende a {total}.\n\nPara cualquier consulta o aclaración no dude en ponerse en contacto con nosotros.\n\nAtentamente,',
        tplFormalBody: 'Estimado/a Sr./Sra.:\n\nTenemos el placer de remitirle la factura {number} relativa a los servicios prestados.\n\nEl importe total asciende a {total}.\n\nLe rogamos proceda a su tramitación conforme a las condiciones pactadas. Quedamos a su disposición para cualquier consulta.\n\nReciba un cordial saludo,',
        tplBriefBody: 'Hola:\n\nAdjuntamos la factura {number}. El importe total es de {total}.\n\nQuedamos a su disposición para cualquier duda.\n\nUn saludo,',
      },
      preview: {
        title: 'Vista previa del PDF', open: 'Ver / Imprimir PDF',
        draft: 'Ver borrador', print: 'Imprimir / Guardar PDF', short: 'PDF',
      },
      emit: {
        title: 'Emitir factura',
        notice: 'Al emitir se le asignará un número correlativo definitivo y quedará registrada. Una factura emitida ya no se puede editar ni eliminar.',
        willBe: 'Se le asignará el número {number}',
        email: 'Enviar por email', emailDesc: 'Emite la factura y la envía al cliente',
        print: 'Imprimir / Guardar PDF', printDesc: 'Emite la factura y abre el PDF',
      },

      // Las palabras del PDF. No se ven en ninguna pantalla: viajan con la
      // petición de imprimir y acaban dentro del documento (ver api/invoicing.js).
      pdf: {
        invoice: 'Factura', rectificative: 'Factura rectificativa',
        draft: 'BORRADOR — no es una factura válida', cancelled: 'ANULADA',
        rectifies: 'Rectifica a la factura',
        number: 'Número', issueDate: 'Fecha', dueDate: 'Vencimiento',
        customer: 'Cliente', taxId: 'CIF/NIF',
        description: 'Concepto', quantity: 'Cant.', unitPrice: 'Precio',
        discount: 'Dto.', taxRate: 'IVA', lineTotal: 'Importe',
        subtotal: 'Base imponible', taxBase: 'Base', tax: 'IVA', retention: 'Retención',
        total: 'Total', paid: 'Cobrado', outstanding: 'Pendiente', rectified: 'Rectificado',
        paymentTerms: 'Forma de pago', paymentMethod: 'Medio de pago',
        bank: 'Datos bancarios', notes: 'Notas',
        // Solo se imprimen donde Veri*Factu está encendido. La leyenda
        // «VERI*FACTU» no está aquí a propósito: la fija el reglamento y no
        // es traducible.
        verifactuNotice: 'Factura verificable en la Agencia Tributaria', fingerprint: 'Huella',
        imported: 'Copia de una factura emitida con otro programa',
      },

      // Por qué no se puede emitir. Llegan por código desde el servidor y se
      // enseñan junto al campo que falta.
      errors: {
        NO_LINES: 'Una factura sin líneas no se puede emitir',
        NO_CUSTOMER: 'Falta a quién va dirigida',
        NO_ISSUE_DATE: 'Falta la fecha',
        RECTIFIES_NOT_ISSUED: 'Solo se rectifica una factura emitida',
        RECTIFICATIVE_CUSTOMER: 'Una rectificativa va al mismo cliente y NIF que la factura que corrige',
        OVER_RECTIFIED: 'Rectifica más de lo que queda de la factura original',
        BAD_TOTAL: 'El total no ha salido como un número',
        NEGATIVE_TOTAL: 'Una factura ordinaria no puede salir en negativo: un abono es una rectificativa',
        ALREADY_ISSUED: 'Esta factura ya está emitida',
        FUTURE_ISSUE_DATE: 'La fecha de la factura no puede ser posterior al día en que se emite',
        ISSUE_DATE_BEFORE_PREVIOUS: 'La fecha no puede ser anterior a la de la última factura emitida de la serie: número y fecha tienen que ir en orden',
        BEING_ISSUED: 'Alguien está emitiendo esta factura ahora mismo',
        BUSY: 'Otra persona está cambiando esta factura ahora mismo; vuelve a intentarlo',
        OVER_OUTSTANDING: 'Es más de lo que queda por cobrar',
        HAS_PAYMENTS: 'Tiene cobros anotados: hay que rectificarla, no anularla',
        HAS_RECTIFICATIONS: 'Ya tiene una rectificativa emitida: anula esa o emite otra',
        IS_DRAFT: 'Un borrador se elimina, no se anula',
        ALREADY_CANCELLED: 'Esta factura ya está anulada',
        NOT_ISSUED: 'Un borrador no se ha mandado a nadie, así que nadie ha podido pagarlo',
        CANCELLED: 'Esta factura está anulada',
        ALREADY_PAID: 'Esta factura ya está cobrada del todo',
        NOTHING_TO_COLLECT: 'En esta factura no hay nada que cobrar',
        INVALID_AMOUNT: 'Un cobro tiene que ser un importe mayor que cero',
        VERIFACTU_DISABLED: 'Veri*Factu no está activado en esta empresa',
        NO_NUMBER: 'Una factura sin número no se puede registrar',
        NO_ISSUER_TAX_ID: 'Falta el NIF de la empresa en Ajustes → Empresa',
        NO_RECORD: 'Esta factura no tiene registro Veri*Factu',
        NOT_SUBMITTABLE: 'Este registro no está pendiente de enviarse',
        NO_CERTIFICATE: 'No hay certificado digital instalado en este servidor',
        BAD_INVOICE_TYPE: 'Una extensión ha devuelto un tipo de factura que el reglamento no define',
        NUMBER_TAKEN: 'Ya hay una factura con este número',
        ORIGIN_ALREADY_IMPORTED: 'Ese registro del otro programa ya se importó con otro número',
        NUMBER_IN_SERIES: 'Ese número tiene la forma de la numeración de este ERP y la serie todavía no lo ha pasado: adelántala en Ajustes → Numeración',
        FUTURE_DATE: 'Una factura importada ya se emitió: la fecha no puede ser futura',
        TOTALS_MISMATCH: 'Las líneas no suman lo que imprimió el otro programa',
      },
      // ── Veri*Factu ────────────────────────────────────────────────────────
      // Apagado en toda instancia hasta que un cliente lo enciende: si no lo
      // está, nada de esto se dibuja. Ver VERIFACTU.md del módulo.
      verifactu: {
        title: 'Veri*Factu',
        subtitle: 'El registro de cada factura emitida y lo que Hacienda ha contestado',
        panelTitle: 'Registro Veri*Factu',
        legend: 'Veri*Factu',
        off: 'Veri*Factu no está activado en esta empresa.',
        notRegistered: 'Esta factura no está en la cadena: es anterior a la activación.',
        qrHint: 'Escaneando este código, el cliente comprueba la factura en la Agencia Tributaria.',
        fingerprint: 'Huella',
        previousFingerprint: 'Huella anterior',
        position: 'Posición en la cadena',
        generatedAt: 'Registrado el',
        csv: 'CSV de la Agencia Tributaria',
        attempts: '{count} intentos',
        nextAttempt: 'Siguiente intento: {date}',
        retry: 'Reenviar a Hacienda',
        retryDone: 'Reenviado',
        first: 'Primer registro de la cadena',
        type: { alta: 'Alta', anulacion: 'Anulación' },
        status: {
          pending: 'Pendiente de envío',
          sent: 'Enviado',
          accepted: 'Aceptado',
          accepted_with_errors: 'Aceptado con avisos',
          rejected: 'Rechazado',
          error: 'No ha podido enviarse',
          not_sent: 'Registrado sin enviar',
        },
        mode: {
          verifactu: 'Se envía cada registro a Hacienda',
          sif: 'El registro se guarda y no se envía',
        },
        environment: { test: 'Entorno de pruebas', production: 'Entorno real' },
        chain: {
          title: 'La cadena',
          length: 'Registros',
          last: 'Último registro',
          lastHash: 'Última huella',
          empty: 'Todavía no se ha registrado ninguna factura.',
        },
        certificate: {
          title: 'Certificado digital',
          ok: 'Instalado',
          missing: 'No hay ninguno instalado en este servidor',
          expires: 'Caduca el {date}',
          expiringSoon: 'Caduca en {days} días',
          expired: 'Caducado',
          unknown: 'No se puede leer la fecha de caducidad',
          reasons: {
            NOT_CONFIGURED: 'Nadie ha indicado dónde está el certificado',
            FILE_NOT_FOUND: 'La ruta configurada no existe',
            NOT_READABLE: 'El servidor no puede leer el fichero',
          },
        },
        queue: {
          title: 'Pendiente de llegar a Hacienda',
          empty: 'No queda nada por enviar.',
          stuck: 'Hay envíos parados desde hace {hours} horas.',
        },
        verify: {
          title: 'Comprobar la cadena',
          explain: 'Recalcula la huella de cada registro y comprueba que enlaza con el anterior.',
          run: 'Comprobar',
          intact: 'Comprobados {count} registros: la cadena está intacta.',
          broken: 'Comprobados {count} registros: {problems} problemas.',
          problems: {
            HASH_MISMATCH: 'La huella no coincide con los datos del registro',
            BROKEN_LINK: 'No enlaza con el registro anterior',
            GAP: 'Falta un registro entre este y el anterior',
          },
        },
        ledger: {
          title: 'El registro de facturación',
          explain: 'Lo que se entrega en una inspección, en el orden en que se escribió.',
          columns: { position: '#', type: 'Tipo', number: 'Factura', date: 'Fecha', total: 'Total', status: 'Estado', fingerprint: 'Huella' },
          empty: 'Todavía no hay registros.',
        },
      },

      notifications: {
        categories: { verifactu: 'Veri*Factu: envíos que no llegan a Hacienda' },
        verifactu: {
          title: 'Veri*Factu necesita atención',
          body: '{stuck} registros sin enviar y {rejected} rechazados.',
        },
      },

      search: { draft: 'Borrador sin número', subtitle: 'Factura' },
    },

    // ── Cómo se llama este módulo en las pantallas de la plataforma ──────────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de facturas son de este módulo. Van aquí para que instalarlo deje
    // esas tres pantallas completas sin tocar la plataforma.
    modules: { invoicing: 'Facturas' },
    assistant: {
      openers: {
        invoicing: {
          'what-are-we-owed': '¿Cuánto nos deben ahora mismo?',
          'overdue-invoices': '¿Qué facturas están vencidas?',
          'invoiced-this-month': '¿Cuánto llevamos facturado este mes?',
        },
      },
    },
    permissions: {
      invoicing: {
        read: 'Ver las facturas y lo que está pendiente de cobro',
        write: 'Escribir y editar borradores de factura',
        issue: 'Emitir facturas y enviarlas al cliente',
        collect: 'Anotar los cobros que van entrando',
        manage: 'Anular y rectificar facturas',
        import: 'Importar facturas emitidas con otro programa (migraciones)',
      },
    },
    settings: {
      // La fila que este módulo añade a «Correo saliente». La plataforma la
      // construye a partir del `outbound` del manifiesto y no puede traducirla
      // —no sabe qué es una factura—, así que el texto lo pone quien sí lo sabe.
      ns: {
        invoicing: {
          title: 'Facturas',
          subtitle: 'Con qué datos nace una factura y qué llega solo desde otros módulos',
          defaultTaxRate: { label: 'IVA con el que nace una línea nueva' },
          taxRates: {
            label: 'Tipos de IVA que usa esta empresa',
            help: 'Son los que ofrece el selector de cada línea.',
          },
          defaultRetentionRate: {
            label: 'Retención con la que nace una factura (IRPF)',
            help: 'Déjalo en 0 salvo que la empresa facture con retención. Se descuenta de la base imponible.',
          },
          showRetention: {
            label: 'Ofrecer la retención (IRPF) al escribir una factura',
            help: 'Enciéndelo si esta empresa factura con retención. Con un porcentaje puesto arriba, la fila sale igualmente.',
          },
          defaultPaymentDays: { label: 'Días desde la fecha de factura hasta el vencimiento' },
          defaultPaymentTerms: { label: 'Forma de pago que sale impresa en una factura nueva' },
          defaultPaymentMethod: { label: 'Medio de pago que sale impreso en una factura nueva' },
          defaultCurrency: { label: 'Moneda con la que se emiten las facturas', help: 'ISO 4217, tres letras.' },
          draftFromQuote: {
            label: 'Abrir un borrador de factura al aceptarse un presupuesto',
            help: 'Solo un borrador: no se emite ni se envía nada hasta que alguien lo dice.',
          },
          draftFromWorkOrder: { label: 'Abrir un borrador de factura al cerrarse una orden de trabajo' },
          defaultHourlyRate: {
            label: 'A cuánto se factura la hora',
            help: 'Las órdenes de trabajo apuntan horas, nunca dinero. Este es el precio con el que entran, y en 0 la línea llega para que alguien la ponga.',
          },
          materialMarkup: {
            label: 'Margen sobre el material que viene de una orden de trabajo',
            help: '1 es a precio de coste, 1,3 le añade un treinta por ciento.',
          },
          labourLineLabel: { label: 'Cómo se llama la línea de las horas en la factura' },
          verifactu: {
            label: 'Llevar el registro Veri*Factu de cada factura emitida',
            help: 'España, RD 1007/2023. Una vez encendido, cada factura queda encadenada a la anterior y ninguna se puede alterar. Léete la guía del módulo antes: apagarlo después no lo deshace.',
          },
          verifactuSubmit: {
            label: 'Enviar cada registro a Hacienda según se escribe',
            help: 'Apagado guarda el registro sin enviarlo, que es la otra forma legal de cumplir y la que más obligaciones tiene. En cualquier caso no sale nada de este servidor hasta que tenga un certificado digital instalado.',
          },
        },
      },
    },
    audit: {
      actions: {
        invoicing: {
          draft_created: 'Borrador de factura creado',
          draft_updated: 'Borrador de factura modificado',
          draft_deleted: 'Borrador de factura eliminado',
          invoice_issued: 'Factura emitida',
          invoice_cancelled: 'Factura anulada',
          rectification_opened: 'Rectificativa abierta',
          payment_registered: 'Cobro anotado',
          payment_removed: 'Cobro retirado',
          invoice_sent: 'Factura enviada al cliente',
          verifactu_registered: 'Factura registrada en Veri*Factu',
          verifactu_cancelled: 'Anulación registrada en Veri*Factu',
          verifactu_resent: 'Registro Veri*Factu reenviado a Hacienda',
          invoice_imported: 'Factura importada de otro programa',
        },
      },
    },
  },

  ca: {
    invoicing: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'una factura vençuda | {count} factures vençudes',
      },
      title: 'Factures',
      dashboard: {
        outstanding: 'Pendent de cobrament',
        outstandingHelp: 'Suma del que s\'ha emès i encara no s\'ha cobrat, en euros, comptant els cobraments parcials.',
        overdue: 'Factures vençudes',
        overdueHelp: 'Quantes factures emeses han passat de la data de venciment sense cobrar-se del tot.',
        invoicedMonth: 'Facturat aquest mes',
        invoicedMonthHelp: 'Total emès des del dia 1 del mes en curs, impostos inclosos.',
        collectedMonth: 'Cobrat aquest mes',
        collectedMonthHelp: 'El que ha entrat des del dia 1 del mes en curs, contra factures emeses.',
        unpaid: 'Per cobrar',
        unpaidHelp: 'Les factures sense cobrar amb el venciment més proper, de la més urgent a la menys. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'El que heu facturat, el que us han pagat i el que falta per cobrar',
      actions: {
        new: 'Nova factura', edit: 'Editar', delete: 'Eliminar esborrany',
        deleteConfirm: 'S’esborra del tot. No té número ni l’ha vist ningú, així que no es perd res més.',
        issue: 'Emetre', cancel: 'Anul·lar', rectify: 'Rectificar',
        payment: 'Registrar cobrament', send: 'Enviar', resend: 'Reenviar', print: 'Descarregar PDF',
        save: 'Desar', close: 'Tancar', back: 'Tornar', confirm: 'Confirmar', cancelAction: 'Cancel·lar',
      },
      filters: {
        oneCustomer: "Només d'un client",

        search: 'Cercar', searchPlaceholder: 'Número, client, concepte…',
        status: 'Estat', allStatuses: 'Tots',
        dateFrom: 'Des de', dateTo: 'Fins a',
      },
      status: {
        draft: 'Esborrany', issued: 'Emesa', partially_paid: 'Cobrada en part',
        overdue: 'Vençuda', paid: 'Cobrada', rectified: 'Rectificada', cancelled: 'Anul·lada',
      },
      series: { invoice: 'Factura', rectificative: 'Factura rectificativa' },
      table: {
        number: 'Número', customer: 'Client', issueDate: 'Data', dueDate: 'Venciment',
        total: 'Total', outstanding: 'Pendent', status: 'Estat',
      },
      kpi: {
        invoiced: 'Facturat', collected: 'Cobrat',
        outstanding: 'Pendent de cobrament', overdue: 'Vençut',
        nextNumber: 'Següent número',
      },
      empty: {
        none: 'Encara no hi ha factures',
        noneHint: 'Escriu la primera. Si teniu pressupostos o ordres de treball, també poden obrir l’esborrany soles.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      list: {
        count: '{count} factures', view: 'Veure detall',
        paid: 'Cobrat', paidShort: 'cobrat {amount}',
      },

      form: {
        newTitle: 'Nova factura', editTitle: 'Editar esborrany',
        saveDraft: 'Desar esborrany', saving: 'Desant…', issuing: 'Emetent…',
        saveAndIssue: 'Desar i emetre',
        subtitle: 'Escriu el que es cobra, i arrossega del catàleg el que calgui',
        sectionInfo: 'La factura', sectionNotes: 'Notes',
        customer: 'Client',
        customerTaxId: 'CIF/NIF', customerAddress: 'Adreça',
        customerEmail: 'Correu per enviar-la',
        project: 'Obra o projecte',
        title: 'Concepte', titlePlaceholder: 'Revisió anual, obra de la nau 2…',
        issueDate: 'Data de factura', dueDate: 'Venciment',
        dueHelp: 'Es calcula des de la data de la factura, no des d’avui.',
        issueHelp: 'Si no la canvies, la factura pren la data del dia en què s’emeti.',
        paymentTerms: 'Forma de pagament', paymentMethod: 'Mitjà de pagament',
        retentionRate: 'Retenció (IRPF)',
        retentionHelp: 'Es descompta de la base imposable, no del total amb IVA.',
        notes: 'Notes per al client', internalNotes: 'Notes internes',
        internalHelp: 'No surten al PDF ni al correu.',
        noteEditor: {
          open: 'Obrir en gran',
          done: 'Fet',
          improve: 'Millorar amb IA', improving: 'Escrivint…',
          improveHint: 'Arregla la redacció sense canviar el que diu, i no hi afegeix cap xifra.',
          undo: 'Desfer',
          printedHint: 'Surt impresa al peu de la factura.',
          printedPlaceholder: 'El que vulguis que llegeixi qui rep la factura…',
          internalPlaceholder: 'Per a vosaltres: què es va acordar, amb qui, què falta…',
        },
        lines: {
          title: 'Línies', add: 'Afegir línia',
          description: 'Concepte', detail: 'Detall', quantity: 'Quantitat', unit: 'Unitat',
          unitPrice: 'Preu', costPrice: 'Cost', margin: 'Marge', marginShort: 'Mrg.',
          discount: 'Dte. %', taxRate: 'IVA %', amount: 'Import',
          remove: 'Treure línia', empty: 'Encara no hi ha línies',
          descriptionPlaceholder: 'Títol del concepte…',
          detailPlaceholder: 'Descripció de la línia (opcional)…',
          unitPlaceholder: 'u., h, m…',
          editMargin: 'Escriu el marge i es calcula el cost',
          noCost: 'Sense cost no hi ha marge a calcular',
          dragToReorder: 'Arrossega per canviar l’ordre',
          dragHint: 'Deixa-hi anar alguna cosa del catàleg per afegir-la com a línia',
          components: '{count} components',
          breakdown: 'Desglossament · {count} components',
          component: 'Component', componentsTotal: 'Suma de components',
        },
        totals: {
          subtotal: 'Base imposable', discount: 'Descomptes', tax: 'IVA',
          retention: 'Retenció', total: 'Total',
          margin: 'Marge mitjà', globalMargin: 'Marge per a totes', applyAll: 'Aplicar',
          marginRequired: 'Escriu un percentatge abans d’aplicar-lo',
          noPricedLines: 'No hi ha cap línia amb preu on aplicar-lo',
          confirmGlobalMargin: 'Es posarà un marge del {percent}% a {count} línies, i se sobreescriurà el cost que ja tinguessin. Continuem?',
        },
        summary: {
          title: 'Resum', customer: 'Client', invoice: 'La factura',
          customerEmpty: 'Tria un client i les seves dades es omplen soles',
          filled: 'Omplert des de la fitxa del client',
          open: 'Obrir el resum', close: 'Plegar el resum',
          linesCount: 'línies', priced: 'amb preu',
        },
        nextNumber: 'En emetre-la li tocarà el {number}',
        picker: {
          open: 'Obrir el catàleg',
          label: 'Catàleg',
          rates: 'Tarifes',
          close: 'Plegar el catàleg',
          allCategories: 'Totes les categories',
          dragHint: 'Arrossega {name} a la factura, o fes clic per afegir-lo',
          noResults: 'Res no coincideix amb la cerca',
          uncategorised: 'Sense categoria',
          materials: 'Materials', search: 'Cercar al catàleg…',
          empty: 'Aquí sortiran els materials i les tarifes del magatzem o de pressupostos',
          loading: 'Carregant el catàleg…',
        },
      },

      invoice: {
        title: 'Factura',
        detailsTitle: 'Detalls de la factura', economicTitle: 'Resum econòmic',
        linesTitle: 'Línies de factura', collected: 'Cobrat',
        outstandingLabel: 'Pendent', collectedPct: '{pct}% cobrat',
        rectifiedLabel: 'Rectificat ({numbers})',
        draftNotice: 'És un esborrany: encara no té número i es pot canviar sencer.',
        issuedNotice: 'Emesa el {date}. Una factura emesa ja no es toca: si cal corregir-la, es rectifica.',
        importedNotice: 'Emesa el {date} amb un altre programa i importada aquí. No forma part del registre Veri*Factu: no es va emetre des d’aquest ERP.',
        cancelledNotice: 'Anul·lada el {date}. El número continua gastat, que és el correcte.',
        issuedBy: 'Emesa per', createdBy: 'Creada per',
        cancelReason: 'Motiu de l’anul·lació',
        rectifies: 'Rectifica la',
        source: {
          label: 'Ve de',
          quote: 'Pressupost {code}', work_order: 'Ordre de treball {code}', manual: 'Escrita a mà', import: 'Importada de {code}', importUnnamed: 'Importada d’un altre programa',
        },
        payments: {
          title: 'Cobraments', none: 'Encara no s’ha cobrat res',
          amount: 'Import', method: 'Mitjà', reference: 'Referència',
          date: 'Data', registeredBy: 'Anotat per', remove: 'Treure aquest cobrament',
          removeConfirm: 'Vols treure aquest cobrament de {amount}? Queda al registre d’auditoria.',
        },
        sent: { never: 'Encara no s’ha enviat', last: 'Enviada per últim cop el {date}', count: '{count} enviaments' },
      },

      issueDialog: {
        title: 'Emetre la factura',
        question: 'Li tocarà el número {number} i s’avisarà la resta de l’ERP.',
        warning: 'A partir d’aquí no es pot editar. Un error es corregeix amb una rectificativa.',
      },
      cancelDialog: {
        title: 'Anul·lar la factura',
        question: 'La factura es queda, marcada com anul·lada, i el seu número continua gastat.',
        reason: 'Motiu', reasonHint: 'Ho llegirà algú que no hi era, d’aquí a tres anys.',
      },
      rectifyDialog: {
        title: 'Rectificar la factura',
        explain: 'S’obre un esborrany nou amb les línies en negatiu. El revises i l’emets tu.',
        reason: 'Què s’està corregint',
      },
      payment: {
        title: 'Registrar un cobrament',
        amount: 'Import', outstanding: 'Queda per cobrar {amount}',
        method: 'Mitjà de pagament', reference: 'Referència', date: 'Data del cobrament',
        notes: 'Notes', settles: 'Amb això queda cobrada del tot',
        totalInvoice: 'Total factura', alreadyPaid: 'Ja cobrat', pending: 'Pendent',
        saving: 'Desant…', confirm: 'Confirmar cobrament',
        methods: {
          transfer: 'Transferència', card: 'Targeta', cash: 'Efectiu',
          direct_debit: 'Domiciliació', cheque: 'Xec', other: 'Altre',
        },
      },
      send: {
        title: 'Enviar la factura',
        to: 'Per a', subject: 'Assumpte', body: 'Missatge',
        subjectDefault: 'Factura {number}',
        draftWithAi: '✦ Redactar', drafting: 'Escrivint…',
        aiHint: 'La IA escriu el missatge. Els imports surten de la factura, no d’ella.',
        attachment: 'Va adjunta en PDF',
        confirm: 'Enviar', sent: 'Factura enviada',
        noAddress: 'No hi ha cap adreça on enviar-la',
        disabled: 'Aquesta instància encara no té el correu configurat. Descarrega el PDF i envia’l tu.',
        templates: 'Plantilla', templateStandard: 'Estàndard', templateFormal: 'Formal', templateBrief: 'Breu',
        language: 'Idioma', improve: 'Millorar amb IA', improving: 'Generant…',
        signature: 'Signatura automàtica', signatureEdit: 'Editar', signatureSave: 'Desar', signatureReset: 'Restablir',
        toHint: 'Prem Retorn o coma per afegir una altra adreça',
        subjectPlaceholder: 'Assumpte del correu…', bodyPlaceholder: 'Escriu el missatge per al client…',
        sending: 'Enviant…',
        tplStandardBody: 'Benvolgut/da client:\n\nLi adjuntem la factura {number} corresponent als serveis prestats.\n\nL’import total és de {total}.\n\nPer a qualsevol consulta o aclariment no dubti a posar-se en contacte amb nosaltres.\n\nCordialment,',
        tplFormalBody: 'Benvolgut/da Sr./Sra.:\n\nTenim el plaer de remetre-li la factura {number} relativa als serveis prestats.\n\nL’import total és de {total}.\n\nLi preguem que procedeixi a la seva tramitació d’acord amb les condicions pactades. Restem a la seva disposició per a qualsevol consulta.\n\nRebi una cordial salutació,',
        tplBriefBody: 'Hola:\n\nAdjuntem la factura {number}. L’import total és de {total}.\n\nRestem a la seva disposició per a qualsevol dubte.\n\nSalutacions,',
      },
      preview: {
        title: 'Vista prèvia del PDF', open: 'Veure / Imprimir PDF',
        draft: 'Veure esborrany', print: 'Imprimir / Desar PDF', short: 'PDF',
      },
      emit: {
        title: 'Emetre factura',
        notice: 'En emetre-la se li assignarà un número correlatiu definitiu i quedarà registrada. Una factura emesa ja no es pot editar ni eliminar.',
        willBe: 'Se li assignarà el número {number}',
        email: 'Enviar per correu', emailDesc: 'Emet la factura i l’envia al client',
        print: 'Imprimir / Desar PDF', printDesc: 'Emet la factura i obre el PDF',
      },

      pdf: {
        invoice: 'Factura', rectificative: 'Factura rectificativa',
        draft: 'ESBORRANY — no és una factura vàlida', cancelled: 'ANUL·LADA',
        rectifies: 'Rectifica la factura',
        number: 'Número', issueDate: 'Data', dueDate: 'Venciment',
        customer: 'Client', taxId: 'CIF/NIF',
        description: 'Concepte', quantity: 'Quant.', unitPrice: 'Preu',
        discount: 'Dte.', taxRate: 'IVA', lineTotal: 'Import',
        subtotal: 'Base imposable', taxBase: 'Base', tax: 'IVA', retention: 'Retenció',
        total: 'Total', paid: 'Cobrat', outstanding: 'Pendent', rectified: 'Rectificat',
        paymentTerms: 'Forma de pagament', paymentMethod: 'Mitjà de pagament',
        bank: 'Dades bancàries', notes: 'Notes',
        verifactuNotice: 'Factura verificable a l’Agència Tributària', fingerprint: 'Empremta',
        imported: 'Còpia d’una factura emesa amb un altre programa',
      },

      errors: {
        NO_LINES: 'Una factura sense línies no es pot emetre',
        NO_CUSTOMER: 'Falta a qui va adreçada',
        NO_ISSUE_DATE: 'Falta la data',
        RECTIFIES_NOT_ISSUED: 'Només es rectifica una factura emesa',
        RECTIFICATIVE_CUSTOMER: 'Una rectificativa va al mateix client i NIF que la factura que corregeix',
        OVER_RECTIFIED: 'Rectifica més del que queda de la factura original',
        BAD_TOTAL: 'El total no ha sortit com un número',
        NEGATIVE_TOTAL: 'Una factura ordinària no pot sortir en negatiu: un abonament és una rectificativa',
        ALREADY_ISSUED: 'Aquesta factura ja està emesa',
        FUTURE_ISSUE_DATE: 'La data de la factura no pot ser posterior al dia en què s’emet',
        ISSUE_DATE_BEFORE_PREVIOUS: 'La data no pot ser anterior a la de l’última factura emesa de la sèrie: número i data han d’anar en ordre',
        BEING_ISSUED: 'Algú està emetent aquesta factura ara mateix',
        BUSY: 'Una altra persona està canviant aquesta factura ara mateix; torna-ho a provar',
        OVER_OUTSTANDING: 'És més del que queda per cobrar',
        HAS_PAYMENTS: 'Té cobraments anotats: cal rectificar-la, no anul·lar-la',
        HAS_RECTIFICATIONS: 'Ja té una rectificativa emesa: anul·la aquella o emet-ne una altra',
        IS_DRAFT: 'Un esborrany s’elimina, no s’anul·la',
        ALREADY_CANCELLED: 'Aquesta factura ja està anul·lada',
        NOT_ISSUED: 'Un esborrany no s’ha enviat a ningú, així que ningú l’ha pogut pagar',
        CANCELLED: 'Aquesta factura està anul·lada',
        ALREADY_PAID: 'Aquesta factura ja està cobrada del tot',
        NOTHING_TO_COLLECT: 'En aquesta factura no hi ha res a cobrar',
        INVALID_AMOUNT: 'Un cobrament ha de ser un import més gran que zero',
        VERIFACTU_DISABLED: 'Veri*Factu no està activat en aquesta empresa',
        NO_NUMBER: 'Una factura sense número no es pot registrar',
        NO_ISSUER_TAX_ID: 'Falta el NIF de l’empresa a Configuració → Empresa',
        NO_RECORD: 'Aquesta factura no té registre Veri*Factu',
        NOT_SUBMITTABLE: 'Aquest registre no està pendent d’enviar-se',
        NO_CERTIFICATE: 'No hi ha cap certificat digital instal·lat en aquest servidor',
        BAD_INVOICE_TYPE: 'Una extensió ha retornat un tipus de factura que el reglament no defineix',
        NUMBER_TAKEN: 'Ja hi ha una factura amb aquest número',
        ORIGIN_ALREADY_IMPORTED: 'Aquest registre de l’altre programa ja es va importar amb un altre número',
        NUMBER_IN_SERIES: 'Aquest número té la forma de la numeració d’aquest ERP i la sèrie encara no l’ha passat: avança-la a Configuració → Numeració',
        FUTURE_DATE: 'Una factura importada ja es va emetre: la data no pot ser futura',
        TOTALS_MISMATCH: 'Les línies no sumen el que va imprimir l’altre programa',
      },
      // ── Veri*Factu ────────────────────────────────────────────────────────
      // Apagat a tota instància fins que un client l’encén: si no ho està, res
      // d’això es dibuixa. Vegeu VERIFACTU.md del mòdul.
      verifactu: {
        title: 'Veri*Factu',
        subtitle: 'El registre de cada factura emesa i el que ha contestat Hisenda',
        panelTitle: 'Registre Veri*Factu',
        legend: 'Veri*Factu',
        off: 'Veri*Factu no està activat en aquesta empresa.',
        notRegistered: 'Aquesta factura no és a la cadena: és anterior a l’activació.',
        qrHint: 'Escanejant aquest codi, el client comprova la factura a l’Agència Tributària.',
        fingerprint: 'Empremta',
        previousFingerprint: 'Empremta anterior',
        position: 'Posició a la cadena',
        generatedAt: 'Registrat el',
        csv: 'CSV de l’Agència Tributària',
        attempts: '{count} intents',
        nextAttempt: 'Següent intent: {date}',
        retry: 'Reenviar a Hisenda',
        retryDone: 'Reenviat',
        first: 'Primer registre de la cadena',
        type: { alta: 'Alta', anulacion: 'Anul·lació' },
        status: {
          pending: 'Pendent d’enviar',
          sent: 'Enviat',
          accepted: 'Acceptat',
          accepted_with_errors: 'Acceptat amb avisos',
          rejected: 'Rebutjat',
          error: 'No s’ha pogut enviar',
          not_sent: 'Registrat sense enviar',
        },
        mode: {
          verifactu: 'S’envia cada registre a Hisenda',
          sif: 'El registre es guarda i no s’envia',
        },
        environment: { test: 'Entorn de proves', production: 'Entorn real' },
        chain: {
          title: 'La cadena',
          length: 'Registres',
          last: 'Últim registre',
          lastHash: 'Última empremta',
          empty: 'Encara no s’ha registrat cap factura.',
        },
        certificate: {
          title: 'Certificat digital',
          ok: 'Instal·lat',
          missing: 'No n’hi ha cap d’instal·lat en aquest servidor',
          expires: 'Caduca el {date}',
          expiringSoon: 'Caduca d’aquí a {days} dies',
          expired: 'Caducat',
          unknown: 'No es pot llegir la data de caducitat',
          reasons: {
            NOT_CONFIGURED: 'Ningú no ha indicat on és el certificat',
            FILE_NOT_FOUND: 'La ruta configurada no existeix',
            NOT_READABLE: 'El servidor no pot llegir el fitxer',
          },
        },
        queue: {
          title: 'Pendent d’arribar a Hisenda',
          empty: 'No queda res per enviar.',
          stuck: 'Hi ha enviaments aturats des de fa {hours} hores.',
        },
        verify: {
          title: 'Comprovar la cadena',
          explain: 'Recalcula l’empremta de cada registre i comprova que enllaça amb l’anterior.',
          run: 'Comprovar',
          intact: 'Comprovats {count} registres: la cadena és intacta.',
          broken: 'Comprovats {count} registres: {problems} problemes.',
          problems: {
            HASH_MISMATCH: 'L’empremta no coincideix amb les dades del registre',
            BROKEN_LINK: 'No enllaça amb el registre anterior',
            GAP: 'Falta un registre entre aquest i l’anterior',
          },
        },
        ledger: {
          title: 'El registre de facturació',
          explain: 'El que es lliura en una inspecció, en l’ordre en què es va escriure.',
          columns: { position: '#', type: 'Tipus', number: 'Factura', date: 'Data', total: 'Total', status: 'Estat', fingerprint: 'Empremta' },
          empty: 'Encara no hi ha registres.',
        },
      },

      notifications: {
        categories: { verifactu: 'Veri*Factu: enviaments que no arriben a Hisenda' },
        verifactu: {
          title: 'Veri*Factu necessita atenció',
          body: '{stuck} registres sense enviar i {rejected} rebutjats.',
        },
      },

      search: { draft: 'Esborrany sense número', subtitle: 'Factura' },
    },

    modules: { invoicing: 'Factures' },
    assistant: {
      openers: {
        invoicing: {
          'what-are-we-owed': 'Quant ens deuen ara mateix?',
          'overdue-invoices': 'Quines factures estan vençudes?',
          'invoiced-this-month': 'Quant portem facturat aquest mes?',
        },
      },
    },
    permissions: {
      invoicing: {
        read: 'Veure les factures i el que està pendent de cobrament',
        write: 'Escriure i editar esborranys de factura',
        issue: 'Emetre factures i enviar-les al client',
        collect: 'Anotar els cobraments que van entrant',
        manage: 'Anul·lar i rectificar factures',
        import: 'Importar factures emeses amb un altre programa (migracions)',
      },
    },
    settings: {
      ns: {
        invoicing: {
          title: 'Factures',
          subtitle: 'Amb quines dades neix una factura i què arriba sol des d’altres mòduls',
          defaultTaxRate: { label: 'IVA amb què neix una línia nova' },
          taxRates: {
            label: 'Tipus d’IVA que fa servir aquesta empresa',
            help: 'Són els que ofereix el selector de cada línia.',
          },
          defaultRetentionRate: {
            label: 'Retenció amb què neix una factura (IRPF)',
            help: 'Deixa-ho a 0 llevat que l’empresa facturi amb retenció. Es descompta de la base imposable.',
          },
          showRetention: {
            label: 'Oferir la retenció (IRPF) en escriure una factura',
            help: 'Encén-ho si aquesta empresa factura amb retenció. Amb un percentatge posat a dalt, la fila surt igualment.',
          },
          defaultPaymentDays: { label: 'Dies des de la data de factura fins al venciment' },
          defaultPaymentTerms: { label: 'Forma de pagament que surt impresa en una factura nova' },
          defaultPaymentMethod: { label: 'Mitjà de pagament que surt imprès en una factura nova' },
          defaultCurrency: { label: 'Moneda amb què s’emeten les factures', help: 'ISO 4217, tres lletres.' },
          draftFromQuote: {
            label: 'Obrir un esborrany de factura en acceptar-se un pressupost',
            help: 'Només un esborrany: no s’emet ni s’envia res fins que algú ho diu.',
          },
          draftFromWorkOrder: { label: 'Obrir un esborrany de factura en tancar-se una ordre de treball' },
          defaultHourlyRate: {
            label: 'A quant es factura l’hora',
            help: 'Les ordres de treball apunten hores, mai diners. Aquest és el preu amb què entren, i a 0 la línia arriba perquè algú la posi.',
          },
          materialMarkup: {
            label: 'Marge sobre el material que ve d’una ordre de treball',
            help: '1 és a preu de cost, 1,3 hi afegeix un trenta per cent.',
          },
          labourLineLabel: { label: 'Com es diu la línia de les hores a la factura' },
          verifactu: {
            label: 'Portar el registre Veri*Factu de cada factura emesa',
            help: 'Espanya, RD 1007/2023. Un cop encès, cada factura queda encadenada a l’anterior i cap no es pot alterar. Llegeix la guia del mòdul abans: apagar-ho després no ho desfà.',
          },
          verifactuSubmit: {
            label: 'Enviar cada registre a Hisenda a mesura que s’escriu',
            help: 'Apagat guarda el registre sense enviar-lo, que és l’altra manera legal de complir i la que té més obligacions. En tot cas no surt res d’aquest servidor fins que hi hagi un certificat digital instal·lat.',
          },
        },
      },
    },
    audit: {
      actions: {
        invoicing: {
          draft_created: 'Esborrany de factura creat',
          draft_updated: 'Esborrany de factura modificat',
          draft_deleted: 'Esborrany de factura eliminat',
          invoice_issued: 'Factura emesa',
          invoice_cancelled: 'Factura anul·lada',
          rectification_opened: 'Rectificativa oberta',
          payment_registered: 'Cobrament anotat',
          payment_removed: 'Cobrament retirat',
          invoice_sent: 'Factura enviada al client',
          verifactu_registered: 'Factura registrada a Veri*Factu',
          verifactu_cancelled: 'Anul·lació registrada a Veri*Factu',
          verifactu_resent: 'Registre Veri*Factu reenviat a Hisenda',
          invoice_imported: 'Factura importada d’un altre programa',
        },
      },
    },
  },

  en: {
    invoicing: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'one overdue invoice | {count} overdue invoices',
      },
      title: 'Invoices',
      dashboard: {
        outstanding: 'Outstanding',
        outstandingHelp: 'What has been issued and not yet collected, in euros, part payments counted.',
        overdue: 'Overdue invoices',
        overdueHelp: 'How many issued invoices are past their due date without being paid in full.',
        invoicedMonth: 'Invoiced this month',
        invoicedMonthHelp: 'Total issued since the 1st of the current month, tax included.',
        collectedMonth: 'Collected this month',
        collectedMonthHelp: 'What has come in since the 1st of the current month, against issued invoices.',
        unpaid: 'To collect',
        unpaidHelp: 'The unpaid invoices falling due soonest, most urgent first. Stretch the card and more fit.',
      },
      subtitle: 'What you have billed, what has been paid, and what is still owed',
      actions: {
        new: 'New invoice', edit: 'Edit', delete: 'Delete draft',
        deleteConfirm: 'It goes for good. It has no number and nobody has seen it, so nothing else is lost.',
        issue: 'Issue', cancel: 'Cancel invoice', rectify: 'Rectify',
        payment: 'Register payment', send: 'Send', resend: 'Send again', print: 'Download PDF',
        save: 'Save', close: 'Close', back: 'Back', confirm: 'Confirm', cancelAction: 'Cancel',
      },
      filters: {
        oneCustomer: 'One customer only',

        search: 'Search', searchPlaceholder: 'Number, customer, description…',
        status: 'State', allStatuses: 'All',
        dateFrom: 'From', dateTo: 'To',
      },
      status: {
        draft: 'Draft', issued: 'Issued', partially_paid: 'Partly paid',
        overdue: 'Overdue', paid: 'Paid', rectified: 'Rectified', cancelled: 'Cancelled',
      },
      series: { invoice: 'Invoice', rectificative: 'Rectificative invoice' },
      table: {
        number: 'Number', customer: 'Customer', issueDate: 'Date', dueDate: 'Due',
        total: 'Total', outstanding: 'Outstanding', status: 'State',
      },
      kpi: {
        invoiced: 'Invoiced', collected: 'Collected',
        outstanding: 'Outstanding', overdue: 'Overdue',
        nextNumber: 'Next number',
      },
      empty: {
        none: 'No invoices yet',
        noneHint: 'Write the first one. If you run quotes or work orders, they can open the draft on their own too.',
        filtered: 'Nothing found',
        filteredHint: 'Try removing a filter or searching for something else.',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      list: {
        count: '{count} invoices', view: 'View',
        paid: 'Paid', paidShort: '{amount} paid',
      },

      form: {
        newTitle: 'New invoice', editTitle: 'Edit draft',
        saveDraft: 'Save draft', saving: 'Saving…', issuing: 'Issuing…',
        saveAndIssue: 'Save and issue',
        subtitle: 'Write what is being charged, and drag in whatever the catalogue has',
        sectionInfo: 'The invoice', sectionNotes: 'Notes',
        customer: 'Customer',
        customerTaxId: 'Tax id', customerAddress: 'Address',
        customerEmail: 'Address to send it to',
        project: 'Job or project',
        title: 'Description', titlePlaceholder: 'Annual service, work on bay 2…',
        issueDate: 'Invoice date', dueDate: 'Due date',
        dueHelp: 'Counted from the invoice date, not from today.',
        issueHelp: 'Leave it as it is and the invoice takes the date of the day it is issued.',
        paymentTerms: 'Payment terms', paymentMethod: 'Payment method',
        retentionRate: 'Withholding',
        retentionHelp: 'Taken off the taxable base, not off the total with VAT.',
        notes: 'Notes for the customer', internalNotes: 'Internal notes',
        internalHelp: 'They appear on neither the PDF nor the email.',
        noteEditor: {
          open: 'Open it big',
          done: 'Done',
          improve: 'Improve with AI', improving: 'Writing…',
          improveHint: 'Fixes the wording without changing what it says, and adds no figure.',
          undo: 'Undo',
          printedHint: 'It is printed at the foot of the invoice.',
          printedPlaceholder: 'Whatever you want the person paying to read…',
          internalPlaceholder: 'For you: what was agreed, with whom, what is missing…',
        },
        lines: {
          title: 'Lines', add: 'Add line',
          description: 'Description', detail: 'Detail', quantity: 'Quantity', unit: 'Unit',
          unitPrice: 'Price', costPrice: 'Cost', margin: 'Margin', marginShort: 'Mrg.',
          discount: 'Disc. %', taxRate: 'VAT %', amount: 'Amount',
          remove: 'Remove line', empty: 'No lines yet',
          descriptionPlaceholder: 'Line title…',
          detailPlaceholder: 'Line description (optional)…',
          unitPlaceholder: 'unit, h, m…',
          editMargin: 'Type the margin and the cost is worked out',
          noCost: 'No cost, so there is no margin to work out',
          dragToReorder: 'Drag to reorder',
          dragHint: 'Drop something from the catalogue here to add it as a line',
          components: '{count} components',
          breakdown: 'Breakdown · {count} components',
          component: 'Component', componentsTotal: 'Components total',
        },
        totals: {
          subtotal: 'Taxable base', discount: 'Discounts', tax: 'VAT',
          retention: 'Withholding', total: 'Total',
          margin: 'Average margin', globalMargin: 'Margin on every line', applyAll: 'Apply',
          marginRequired: 'Type a percentage before applying it',
          noPricedLines: 'There is no priced line to apply it to',
          confirmGlobalMargin: 'This sets a {percent}% margin on {count} lines and overwrites the costs they already had. Go ahead?',
        },
        summary: {
          title: 'Summary', customer: 'Customer', invoice: 'The invoice',
          customerEmpty: 'Pick a customer and their details fill themselves in',
          filled: 'Filled in from the customer file',
          open: 'Open the summary', close: 'Fold the summary',
          linesCount: 'lines', priced: 'priced',
        },
        nextNumber: 'It will be numbered {number} when issued',
        picker: {
          open: 'Open the catalogue',
          label: 'Catalogue',
          rates: 'Rates',
          close: 'Collapse the catalogue',
          allCategories: 'Every category',
          dragHint: 'Drag {name} onto the invoice, or click to add it',
          noResults: 'Nothing matches the search',
          uncategorised: 'No category',
          materials: 'Materials', search: 'Search the catalogue…',
          empty: 'Materials and rates from the warehouse or from quotes appear here',
          loading: 'Loading the catalogue…',
        },
      },

      invoice: {
        title: 'Invoice',
        detailsTitle: 'Invoice details', economicTitle: 'Money',
        linesTitle: 'Invoice lines', collected: 'Collected',
        outstandingLabel: 'Outstanding', collectedPct: '{pct}% collected',
        rectifiedLabel: 'Rectified ({numbers})',
        draftNotice: 'This is a draft: it has no number yet and can be changed completely.',
        issuedNotice: 'Issued on {date}. An issued invoice is not touched: a mistake is corrected with a rectificative one.',
        importedNotice: 'Issued on {date} with another program and imported here. It is not in the Veri*Factu register: it was not issued from this ERP.',
        cancelledNotice: 'Cancelled on {date}. The number stays spent, which is correct.',
        issuedBy: 'Issued by', createdBy: 'Created by',
        cancelReason: 'Reason for cancelling',
        rectifies: 'Corrects invoice',
        source: {
          label: 'Comes from',
          quote: 'Quote {code}', work_order: 'Work order {code}', manual: 'Typed by hand', import: 'Imported from {code}', importUnnamed: 'Imported from another program',
        },
        payments: {
          title: 'Payments', none: 'Nothing collected yet',
          amount: 'Amount', method: 'Method', reference: 'Reference',
          date: 'Date', registeredBy: 'Registered by', remove: 'Remove this payment',
          removeConfirm: 'Remove this payment of {amount}? It stays in the audit trail.',
        },
        sent: { never: 'Not sent yet', last: 'Last sent on {date}', count: '{count} times sent' },
      },

      issueDialog: {
        title: 'Issue the invoice',
        question: 'It will be numbered {number} and the rest of the ERP will be told.',
        warning: 'After that it cannot be edited. A mistake is corrected with a rectificative invoice.',
      },
      cancelDialog: {
        title: 'Cancel the invoice',
        question: 'The invoice stays, marked cancelled, and its number stays spent.',
        reason: 'Reason', reasonHint: 'Somebody who was not here will read this in three years.',
      },
      rectifyDialog: {
        title: 'Rectify the invoice',
        explain: 'A new draft opens with the lines negated. You check it and issue it yourself.',
        reason: 'What is being corrected',
      },
      payment: {
        title: 'Register a payment',
        amount: 'Amount', outstanding: '{amount} still outstanding',
        method: 'Payment method', reference: 'Reference', date: 'Date received',
        notes: 'Notes', settles: 'This settles the invoice',
        totalInvoice: 'Invoice total', alreadyPaid: 'Already collected', pending: 'Outstanding',
        saving: 'Saving…', confirm: 'Confirm payment',
        methods: {
          transfer: 'Transfer', card: 'Card', cash: 'Cash',
          direct_debit: 'Direct debit', cheque: 'Cheque', other: 'Other',
        },
      },
      send: {
        title: 'Send the invoice',
        to: 'To', subject: 'Subject', body: 'Message',
        subjectDefault: 'Invoice {number}',
        draftWithAi: '✦ Draft it', drafting: 'Writing…',
        aiHint: 'The model writes the message. The figures come from the invoice, not from it.',
        attachment: 'The PDF goes attached',
        confirm: 'Send', sent: 'Invoice sent',
        noAddress: 'There is no address to send it to',
        disabled: 'This instance has no mail configured yet. Download the PDF and send it yourself.',
        templates: 'Template', templateStandard: 'Standard', templateFormal: 'Formal', templateBrief: 'Brief',
        language: 'Language', improve: 'Improve with AI', improving: 'Writing…',
        signature: 'Signature', signatureEdit: 'Edit', signatureSave: 'Save', signatureReset: 'Reset',
        toHint: 'Press Enter or comma to add another address',
        subjectPlaceholder: 'Email subject…', bodyPlaceholder: 'Write the message for the customer…',
        sending: 'Sending…',
        tplStandardBody: 'Dear customer,\n\nPlease find attached invoice {number} for the services provided.\n\nThe total amount is {total}.\n\nDo not hesitate to contact us with any questions.\n\nKind regards,',
        tplFormalBody: 'Dear Sir or Madam,\n\nWe are pleased to send you invoice {number} for the services provided.\n\nThe total amount is {total}.\n\nWe kindly ask you to process it under the agreed terms. We remain at your disposal for any questions.\n\nYours faithfully,',
        tplBriefBody: 'Hello,\n\nInvoice {number} is attached. The total is {total}.\n\nWe are happy to help with any questions.\n\nBest regards,',
      },
      preview: {
        title: 'PDF preview', open: 'View / print PDF',
        draft: 'View draft', print: 'Print / save PDF', short: 'PDF',
      },
      emit: {
        title: 'Issue the invoice',
        notice: 'Issuing assigns the next number in the sequence and puts it on the record. An issued invoice can no longer be edited or deleted.',
        willBe: 'It will be numbered {number}',
        email: 'Send by email', emailDesc: 'Issues the invoice and sends it to the customer',
        print: 'Print / save PDF', printDesc: 'Issues the invoice and opens the PDF',
      },

      pdf: {
        invoice: 'Invoice', rectificative: 'Rectificative invoice',
        draft: 'DRAFT — not a valid invoice', cancelled: 'CANCELLED',
        rectifies: 'Corrects invoice',
        number: 'Number', issueDate: 'Issue date', dueDate: 'Due date',
        customer: 'Customer', taxId: 'Tax id',
        description: 'Description', quantity: 'Qty', unitPrice: 'Price',
        discount: 'Disc.', taxRate: 'VAT', lineTotal: 'Amount',
        subtotal: 'Subtotal', taxBase: 'Base', tax: 'VAT', retention: 'Withholding',
        total: 'Total', paid: 'Paid', outstanding: 'Outstanding', rectified: 'Rectified',
        paymentTerms: 'Payment terms', paymentMethod: 'Payment method',
        bank: 'Bank details', notes: 'Notes',
        verifactuNotice: 'Invoice verifiable at the tax office', fingerprint: 'Fingerprint',
        imported: 'Copy of an invoice issued with another program',
      },

      errors: {
        NO_LINES: 'An invoice with no lines cannot be issued',
        NO_CUSTOMER: 'It is not addressed to anybody',
        NO_ISSUE_DATE: 'The date is missing',
        RECTIFIES_NOT_ISSUED: 'Only an issued invoice can be rectified',
        RECTIFICATIVE_CUSTOMER: 'A rectificative invoice goes to the same customer and tax id as the one it corrects',
        OVER_RECTIFIED: 'That rectifies more than is left of the original invoice',
        BAD_TOTAL: 'The total did not come out as a number',
        NEGATIVE_TOTAL: 'An ordinary invoice cannot total less than zero: a credit is a rectificative invoice',
        ALREADY_ISSUED: 'This invoice has already been issued',
        FUTURE_ISSUE_DATE: 'An invoice cannot be dated after the day it is issued',
        ISSUE_DATE_BEFORE_PREVIOUS: 'The date cannot be earlier than the last invoice issued in the series: numbers and dates must stay in order',
        BEING_ISSUED: 'Somebody is issuing this invoice right now',
        BUSY: 'Somebody else is changing this invoice right now; try again',
        OVER_OUTSTANDING: 'That is more than is outstanding',
        HAS_PAYMENTS: 'It has payments against it: rectify it rather than cancelling it',
        HAS_RECTIFICATIONS: 'A rectificative invoice has already been issued against it: cancel that one or issue another',
        IS_DRAFT: 'A draft is deleted, not cancelled',
        ALREADY_CANCELLED: 'This invoice is already cancelled',
        NOT_ISSUED: 'A draft has not been sent to anybody, so nobody can have paid it',
        CANCELLED: 'This invoice was cancelled',
        ALREADY_PAID: 'This invoice is already settled',
        NOTHING_TO_COLLECT: 'There is nothing to collect on this invoice',
        INVALID_AMOUNT: 'A payment has to be an amount greater than zero',
        VERIFACTU_DISABLED: 'Veri*Factu is not switched on for this company',
        NO_NUMBER: 'An invoice with no number cannot be registered',
        NO_ISSUER_TAX_ID: 'The company tax id is missing under Settings → Company',
        NO_RECORD: 'This invoice has no Veri*Factu record',
        NOT_SUBMITTABLE: 'This record is not waiting to be sent',
        NO_CERTIFICATE: 'No digital certificate is installed on this server',
        BAD_INVOICE_TYPE: 'An extension returned an invoice type the regulation does not define',
        NUMBER_TAKEN: 'There is already an invoice with this number',
        ORIGIN_ALREADY_IMPORTED: 'That record of the other program was already imported under another number',
        NUMBER_IN_SERIES: 'That number has the shape of this ERP’s own numbering and the series has not reached it yet: move it forward in Settings → Numbering',
        FUTURE_DATE: 'An imported invoice was already issued: its date cannot be in the future',
        TOTALS_MISMATCH: 'The lines do not add up to what the other program printed',
      },
      // ── Veri*Factu ────────────────────────────────────────────────────────
      // Off on every instance until a client switches it on: with it off, none
      // of this is drawn. See the module's VERIFACTU.md.
      verifactu: {
        title: 'Veri*Factu',
        subtitle: 'The register of every invoice issued, and what the tax office answered',
        panelTitle: 'Veri*Factu record',
        legend: 'Veri*Factu',
        off: 'Veri*Factu is not switched on for this company.',
        notRegistered: 'This invoice is not in the chain: it predates the switch-on.',
        qrHint: 'Scanning this code checks the invoice against the tax office.',
        fingerprint: 'Fingerprint',
        previousFingerprint: 'Previous fingerprint',
        position: 'Position in the chain',
        generatedAt: 'Registered on',
        csv: 'Tax office receipt (CSV)',
        attempts: '{count} attempts',
        nextAttempt: 'Next attempt: {date}',
        retry: 'Send again',
        retryDone: 'Sent again',
        first: 'First record of the chain',
        type: { alta: 'Issue', anulacion: 'Void' },
        status: {
          pending: 'Waiting to be sent',
          sent: 'Sent',
          accepted: 'Accepted',
          accepted_with_errors: 'Accepted with warnings',
          rejected: 'Rejected',
          error: 'Could not be sent',
          not_sent: 'Registered, not sent',
        },
        mode: {
          verifactu: 'Every record is sent to the tax office',
          sif: 'The register is kept and not sent',
        },
        environment: { test: 'Test environment', production: 'Live environment' },
        chain: {
          title: 'The chain',
          length: 'Records',
          last: 'Last record',
          lastHash: 'Last fingerprint',
          empty: 'No invoice has been registered yet.',
        },
        certificate: {
          title: 'Digital certificate',
          ok: 'Installed',
          missing: 'None is installed on this server',
          expires: 'Expires on {date}',
          expiringSoon: 'Expires in {days} days',
          expired: 'Expired',
          unknown: 'The expiry date cannot be read',
          reasons: {
            NOT_CONFIGURED: 'Nobody has said where the certificate is',
            FILE_NOT_FOUND: 'The configured path does not exist',
            NOT_READABLE: 'The server cannot read the file',
          },
        },
        queue: {
          title: 'Still to reach the tax office',
          empty: 'Nothing is waiting to be sent.',
          stuck: 'Submissions have been stuck for {hours} hours.',
        },
        verify: {
          title: 'Check the chain',
          explain: 'Recomputes every fingerprint and checks that each record links to the one before it.',
          run: 'Check',
          intact: '{count} records checked: the chain is intact.',
          broken: '{count} records checked: {problems} problems.',
          problems: {
            HASH_MISMATCH: 'The fingerprint does not match the record',
            BROKEN_LINK: 'It does not link to the record before it',
            GAP: 'A record is missing between this one and the last',
          },
        },
        ledger: {
          title: 'The register of invoices',
          explain: 'What is handed over at an inspection, in the order it was written.',
          columns: { position: '#', type: 'Kind', number: 'Invoice', date: 'Date', total: 'Total', status: 'State', fingerprint: 'Fingerprint' },
          empty: 'There are no records yet.',
        },
      },

      notifications: {
        categories: { verifactu: 'Veri*Factu: filings that are not getting through' },
        verifactu: {
          title: 'Veri*Factu needs attention',
          body: '{stuck} records unsent and {rejected} rejected.',
        },
      },

      search: { draft: 'Draft with no number', subtitle: 'Invoice' },
    },

    modules: { invoicing: 'Invoices' },
    assistant: {
      openers: {
        invoicing: {
          'what-are-we-owed': 'How much are we owed right now?',
          'overdue-invoices': 'Which invoices are overdue?',
          'invoiced-this-month': 'How much have we invoiced this month?',
        },
      },
    },
    permissions: {
      invoicing: {
        read: 'See the invoices and what is outstanding',
        write: 'Write and edit invoice drafts',
        issue: 'Issue invoices and send them to the customer',
        collect: 'Register the payments as they come in',
        manage: 'Cancel and rectify invoices',
        import: 'Import invoices issued with another program (migrations)',
      },
    },
    settings: {
      ns: {
        invoicing: {
          title: 'Invoices',
          subtitle: 'What an invoice starts as, and what arrives on its own from other modules',
          defaultTaxRate: { label: 'VAT rate a new line starts at' },
          taxRates: {
            label: 'VAT rates this company uses',
            help: 'They are what the rate selector offers on each line.',
          },
          defaultRetentionRate: {
            label: 'Withholding a new invoice starts with',
            help: 'Leave it at 0 unless the company invoices with a withholding. It comes off the taxable base.',
          },
          showRetention: {
            label: 'Offer the withholding (IRPF) when writing an invoice',
            help: 'Turn it on if this company invoices with a withholding. A rate set above shows the row anyway.',
          },
          defaultPaymentDays: { label: 'Days from the invoice date to the due date' },
          defaultPaymentTerms: { label: 'Payment terms printed on a new invoice' },
          defaultPaymentMethod: { label: 'Payment method printed on a new invoice' },
          defaultCurrency: { label: 'Currency invoices are issued in', help: 'ISO 4217, three letters.' },
          draftFromQuote: {
            label: 'Open a draft invoice when a quote is accepted',
            help: 'A draft only: nothing is issued and nothing is sent until somebody says so.',
          },
          draftFromWorkOrder: { label: 'Open a draft invoice when a work order is completed' },
          defaultHourlyRate: {
            label: 'What an hour is invoiced at',
            help: 'Work orders report hours, never money. This is what those hours come in priced at, and at 0 the line arrives for somebody to price.',
          },
          materialMarkup: {
            label: 'Markup on material coming from a work order',
            help: '1 is at cost, 1.3 adds thirty per cent.',
          },
          labourLineLabel: { label: 'What the hours line is called on the invoice' },
          verifactu: {
            label: 'Keep the Veri*Factu register of every invoice issued',
            help: 'Spain, RD 1007/2023. Once on, each invoice is chained to the one before it and none of them can be altered. Read the module guide first: switching it off afterwards does not undo it.',
          },
          verifactuSubmit: {
            label: 'Send each record to the tax office as it is written',
            help: 'Off keeps the register without sending it, which is the other lawful way to comply and the one with more obligations. Either way nothing leaves this server until a digital certificate is installed on it.',
          },
        },
      },
    },
    audit: {
      actions: {
        invoicing: {
          draft_created: 'Invoice draft created',
          draft_updated: 'Invoice draft changed',
          draft_deleted: 'Invoice draft deleted',
          invoice_issued: 'Invoice issued',
          invoice_cancelled: 'Invoice cancelled',
          rectification_opened: 'Rectification opened',
          payment_registered: 'Payment registered',
          payment_removed: 'Payment removed',
          invoice_sent: 'Invoice sent to the customer',
          verifactu_registered: 'Invoice registered in Veri*Factu',
          verifactu_cancelled: 'Void registered in Veri*Factu',
          verifactu_resent: 'Veri*Factu record sent again',
          invoice_imported: 'Invoice imported from another program',
        },
      },
    },
  },

  fr: {
    invoicing: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'une facture échue | {count} factures échues',
      },
      title: 'Factures',
      dashboard: {
        outstanding: 'Restant à encaisser',
        outstandingHelp: 'Ce qui a été émis et pas encore encaissé, en euros, acomptes compris.',
        overdue: 'Factures échues',
        overdueHelp: 'Combien de factures émises ont dépassé leur échéance sans être réglées entièrement.',
        invoicedMonth: 'Facturé ce mois-ci',
        invoicedMonthHelp: 'Total émis depuis le 1er du mois en cours, taxes comprises.',
        collectedMonth: 'Encaissé ce mois-ci',
        collectedMonthHelp: 'Ce qui est rentré depuis le 1er du mois en cours, sur des factures émises.',
        unpaid: 'À encaisser',
        unpaidHelp: 'Les factures impayées dont l\'échéance arrive le plus tôt, de la plus urgente à la moins. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Ce que vous avez facturé, ce qui a été payé et ce qui reste dû',
      actions: {
        new: 'Nouvelle facture', edit: 'Modifier', delete: 'Supprimer le brouillon',
        deleteConfirm: 'Elle part définitivement. Elle n’a pas de numéro et personne ne l’a vue, donc rien d’autre n’est perdu.',
        issue: 'Émettre', cancel: 'Annuler la facture', rectify: 'Rectifier',
        payment: 'Enregistrer un règlement', send: 'Envoyer', resend: 'Renvoyer', print: 'Télécharger le PDF',
        save: 'Enregistrer', close: 'Fermer', back: 'Retour', confirm: 'Confirmer', cancelAction: 'Annuler',
      },
      filters: {
        oneCustomer: 'Un seul client',

        search: 'Rechercher', searchPlaceholder: 'Numéro, client, objet…',
        status: 'État', allStatuses: 'Tous',
        dateFrom: 'Du', dateTo: 'Au',
      },
      status: {
        draft: 'Brouillon', issued: 'Émise', partially_paid: 'Partiellement réglée',
        overdue: 'Échue', paid: 'Réglée', rectified: 'Rectifiée', cancelled: 'Annulée',
      },
      series: { invoice: 'Facture', rectificative: 'Facture rectificative' },
      table: {
        number: 'Numéro', customer: 'Client', issueDate: 'Date', dueDate: 'Échéance',
        total: 'Total', outstanding: 'Restant dû', status: 'État',
      },
      kpi: {
        invoiced: 'Facturé', collected: 'Encaissé',
        outstanding: 'Restant dû', overdue: 'Échu',
        nextNumber: 'Prochain numéro',
      },
      empty: {
        none: 'Aucune facture pour le moment',
        noneHint: 'Écrivez la première. Si vous avez des devis ou des ordres de travail, ils peuvent aussi ouvrir le brouillon seuls.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      list: {
        count: '{count} factures', view: 'Voir le détail',
        paid: 'Encaissé', paidShort: '{amount} encaissés',
      },

      form: {
        newTitle: 'Nouvelle facture', editTitle: 'Modifier le brouillon',
        saveDraft: 'Enregistrer le brouillon', saving: 'Enregistrement…', issuing: 'Émission…',
        saveAndIssue: 'Enregistrer et émettre',
        subtitle: 'Écrivez ce qui est facturé, et glissez du catalogue ce qu’il vous faut',
        sectionInfo: 'La facture', sectionNotes: 'Notes',
        customer: 'Client',
        customerTaxId: 'N° de TVA', customerAddress: 'Adresse',
        customerEmail: 'Adresse pour l’envoi',
        project: 'Chantier ou projet',
        title: 'Objet', titlePlaceholder: 'Révision annuelle, travaux du hall 2…',
        issueDate: 'Date de facture', dueDate: 'Échéance',
        dueHelp: 'Comptée depuis la date de la facture, pas depuis aujourd’hui.',
        issueHelp: 'Sans modification, la facture prend la date du jour de son émission.',
        paymentTerms: 'Conditions de paiement', paymentMethod: 'Moyen de paiement',
        retentionRate: 'Retenue',
        retentionHelp: 'Déduite de la base imposable, pas du total TTC.',
        notes: 'Notes pour le client', internalNotes: 'Notes internes',
        internalHelp: 'Elles n’apparaissent ni sur le PDF ni dans le courriel.',
        noteEditor: {
          open: 'Ouvrir en grand',
          done: 'Terminé',
          improve: 'Améliorer avec l’IA', improving: 'Rédaction…',
          improveHint: 'Corrige la formulation sans changer ce qui est dit, et n’ajoute aucun chiffre.',
          undo: 'Annuler',
          printedHint: 'Elle est imprimée au bas de la facture.',
          printedPlaceholder: 'Ce que vous voulez que lise la personne qui reçoit la facture…',
          internalPlaceholder: 'Pour vous : ce qui a été convenu, avec qui, ce qui manque…',
        },
        lines: {
          title: 'Lignes', add: 'Ajouter une ligne',
          description: 'Désignation', detail: 'Détail', quantity: 'Quantité', unit: 'Unité',
          unitPrice: 'Prix', costPrice: 'Coût', margin: 'Marge', marginShort: 'Mrg.',
          discount: 'Rem. %', taxRate: 'TVA %', amount: 'Montant',
          remove: 'Retirer la ligne', empty: 'Aucune ligne pour le moment',
          descriptionPlaceholder: 'Intitulé de la ligne…',
          detailPlaceholder: 'Description de la ligne (facultatif)…',
          unitPlaceholder: 'u., h, m…',
          editMargin: 'Saisissez la marge et le coût est calculé',
          noCost: 'Sans coût, il n’y a pas de marge à calculer',
          dragToReorder: 'Faites glisser pour réordonner',
          dragHint: 'Déposez ici un élément du catalogue pour l’ajouter comme ligne',
          components: '{count} composants',
          breakdown: 'Détail · {count} composants',
          component: 'Composant', componentsTotal: 'Total des composants',
        },
        totals: {
          subtotal: 'Base imposable', discount: 'Remises', tax: 'TVA',
          retention: 'Retenue', total: 'Total',
          margin: 'Marge moyenne', globalMargin: 'Marge sur toutes les lignes', applyAll: 'Appliquer',
          marginRequired: 'Saisissez un pourcentage avant de l’appliquer',
          noPricedLines: 'Aucune ligne chiffrée à laquelle l’appliquer',
          confirmGlobalMargin: 'Une marge de {percent}% sera appliquée à {count} lignes, et les coûts déjà saisis seront écrasés. On continue ?',
        },
        summary: {
          title: 'Récapitulatif', customer: 'Client', invoice: 'La facture',
          customerEmpty: 'Choisissez un client et ses données se remplissent seules',
          filled: 'Rempli depuis la fiche du client',
          open: 'Ouvrir le résumé', close: 'Replier le résumé',
          linesCount: 'lignes', priced: 'chiffrées',
        },
        nextNumber: 'À l’émission elle portera le {number}',
        picker: {
          open: 'Ouvrir le catalogue',
          label: 'Catalogue',
          rates: 'Tarifs',
          close: 'Replier le catalogue',
          allCategories: 'Toutes les catégories',
          dragHint: 'Faites glisser {name} sur la facture, ou cliquez pour l’ajouter',
          noResults: 'Rien ne correspond à la recherche',
          uncategorised: 'Sans catégorie',
          materials: 'Matériaux', search: 'Rechercher dans le catalogue…',
          empty: 'Les matériaux et les tarifs du magasin ou des devis apparaîtront ici',
          loading: 'Chargement du catalogue…',
        },
      },

      invoice: {
        title: 'Facture',
        detailsTitle: 'Détails de la facture', economicTitle: 'Récapitulatif',
        linesTitle: 'Lignes de la facture', collected: 'Encaissé',
        outstandingLabel: 'Restant dû', collectedPct: '{pct}% encaissés',
        rectifiedLabel: 'Rectifié ({numbers})',
        draftNotice: 'C’est un brouillon : il n’a pas encore de numéro et peut être modifié entièrement.',
        issuedNotice: 'Émise le {date}. Une facture émise ne se retouche pas : une erreur se corrige par une rectificative.',
        importedNotice: 'Émise le {date} avec un autre logiciel et importée ici. Elle ne figure pas au registre Veri*Factu : elle n’a pas été émise depuis cet ERP.',
        cancelledNotice: 'Annulée le {date}. Le numéro reste consommé, ce qui est correct.',
        issuedBy: 'Émise par', createdBy: 'Créée par',
        cancelReason: 'Motif de l’annulation',
        rectifies: 'Rectifie la facture',
        source: {
          label: 'Provient de',
          quote: 'Devis {code}', work_order: 'Ordre de travail {code}', manual: 'Saisie à la main', import: 'Importée de {code}', importUnnamed: 'Importée d’un autre logiciel',
        },
        payments: {
          title: 'Règlements', none: 'Rien d’encaissé pour le moment',
          amount: 'Montant', method: 'Moyen', reference: 'Référence',
          date: 'Date', registeredBy: 'Saisi par', remove: 'Retirer ce règlement',
          removeConfirm: 'Retirer ce règlement de {amount} ? Il reste dans le journal d’audit.',
        },
        sent: { never: 'Pas encore envoyée', last: 'Envoyée pour la dernière fois le {date}', count: '{count} envois' },
      },

      issueDialog: {
        title: 'Émettre la facture',
        question: 'Elle portera le numéro {number} et le reste de l’ERP en sera informé.',
        warning: 'Ensuite elle ne peut plus être modifiée. Une erreur se corrige par une rectificative.',
      },
      cancelDialog: {
        title: 'Annuler la facture',
        question: 'La facture reste, marquée annulée, et son numéro reste consommé.',
        reason: 'Motif', reasonHint: 'Quelqu’un qui n’était pas là le lira dans trois ans.',
      },
      rectifyDialog: {
        title: 'Rectifier la facture',
        explain: 'Un nouveau brouillon s’ouvre avec les lignes en négatif. Vous le vérifiez et l’émettez vous-même.',
        reason: 'Ce qui est corrigé',
      },
      payment: {
        title: 'Enregistrer un règlement',
        amount: 'Montant', outstanding: 'Il reste {amount} à encaisser',
        method: 'Moyen de paiement', reference: 'Référence', date: 'Date du règlement',
        notes: 'Notes', settles: 'La facture est alors soldée',
        totalInvoice: 'Total de la facture', alreadyPaid: 'Déjà encaissé', pending: 'Restant dû',
        saving: 'Enregistrement…', confirm: 'Confirmer le règlement',
        methods: {
          transfer: 'Virement', card: 'Carte', cash: 'Espèces',
          direct_debit: 'Prélèvement', cheque: 'Chèque', other: 'Autre',
        },
      },
      send: {
        title: 'Envoyer la facture',
        to: 'À', subject: 'Objet', body: 'Message',
        subjectDefault: 'Facture {number}',
        draftWithAi: '✦ Rédiger', drafting: 'Rédaction…',
        aiHint: 'Le modèle écrit le message. Les montants viennent de la facture, pas de lui.',
        attachment: 'Le PDF part en pièce jointe',
        confirm: 'Envoyer', sent: 'Facture envoyée',
        noAddress: 'Aucune adresse où l’envoyer',
        disabled: 'Cette instance n’a pas encore de messagerie configurée. Téléchargez le PDF et envoyez-le vous-même.',
        templates: 'Modèle', templateStandard: 'Standard', templateFormal: 'Formel', templateBrief: 'Bref',
        language: 'Langue', improve: 'Améliorer avec l’IA', improving: 'Rédaction…',
        signature: 'Signature automatique', signatureEdit: 'Modifier', signatureSave: 'Enregistrer', signatureReset: 'Réinitialiser',
        toHint: 'Appuyez sur Entrée ou virgule pour ajouter une autre adresse',
        subjectPlaceholder: 'Objet du courriel…', bodyPlaceholder: 'Écrivez le message pour le client…',
        sending: 'Envoi…',
        tplStandardBody: 'Madame, Monsieur,\n\nVeuillez trouver ci-jointe la facture {number} correspondant aux services rendus.\n\nLe montant total s’élève à {total}.\n\nN’hésitez pas à nous contacter pour toute question.\n\nCordialement,',
        tplFormalBody: 'Madame, Monsieur,\n\nNous avons le plaisir de vous adresser la facture {number} relative aux services rendus.\n\nLe montant total s’élève à {total}.\n\nNous vous prions de bien vouloir procéder à son règlement selon les conditions convenues. Nous restons à votre disposition pour toute question.\n\nVeuillez agréer nos salutations distinguées,',
        tplBriefBody: 'Bonjour,\n\nLa facture {number} est en pièce jointe. Le montant total est de {total}.\n\nNous restons disponibles pour toute question.\n\nBien cordialement,',
      },
      preview: {
        title: 'Aperçu du PDF', open: 'Voir / imprimer le PDF',
        draft: 'Voir le brouillon', print: 'Imprimer / enregistrer le PDF', short: 'PDF',
      },
      emit: {
        title: 'Émettre la facture',
        notice: 'L’émission attribue le numéro suivant de la série et enregistre la facture. Une facture émise ne peut plus être modifiée ni supprimée.',
        willBe: 'Elle portera le numéro {number}',
        email: 'Envoyer par courriel', emailDesc: 'Émet la facture et l’envoie au client',
        print: 'Imprimer / enregistrer le PDF', printDesc: 'Émet la facture et ouvre le PDF',
      },

      pdf: {
        invoice: 'Facture', rectificative: 'Facture rectificative',
        draft: 'BROUILLON — ce n’est pas une facture valable', cancelled: 'ANNULÉE',
        rectifies: 'Rectifie la facture',
        number: 'Numéro', issueDate: 'Date', dueDate: 'Échéance',
        customer: 'Client', taxId: 'N° de TVA',
        description: 'Désignation', quantity: 'Qté', unitPrice: 'Prix',
        discount: 'Rem.', taxRate: 'TVA', lineTotal: 'Montant',
        subtotal: 'Base imposable', taxBase: 'Base', tax: 'TVA', retention: 'Retenue',
        total: 'Total', paid: 'Réglé', outstanding: 'Restant dû', rectified: 'Rectifié',
        paymentTerms: 'Conditions de paiement', paymentMethod: 'Moyen de paiement',
        bank: 'Coordonnées bancaires', notes: 'Notes',
        verifactuNotice: 'Facture vérifiable auprès du fisc', fingerprint: 'Empreinte',
        imported: 'Copie d’une facture émise avec un autre logiciel',
      },

      errors: {
        NO_LINES: 'Une facture sans ligne ne peut pas être émise',
        NO_CUSTOMER: 'Elle n’est adressée à personne',
        NO_ISSUE_DATE: 'La date manque',
        RECTIFIES_NOT_ISSUED: 'Seule une facture émise peut être rectifiée',
        RECTIFICATIVE_CUSTOMER: 'Une facture rectificative va au même client et au même numéro fiscal que celle qu\'elle corrige',
        OVER_RECTIFIED: 'Cela rectifie plus que ce qui reste de la facture originale',
        BAD_TOTAL: 'Le total n’est pas ressorti comme un nombre',
        NEGATIVE_TOTAL: 'Une facture ordinaire ne peut pas être négative : un avoir est une facture rectificative',
        ALREADY_ISSUED: 'Cette facture est déjà émise',
        FUTURE_ISSUE_DATE: 'La date de la facture ne peut pas être postérieure au jour de son émission',
        ISSUE_DATE_BEFORE_PREVIOUS: 'La date ne peut pas être antérieure à celle de la dernière facture émise de la série : numéros et dates doivent rester dans l’ordre',
        BEING_ISSUED: 'Quelqu’un est en train d’émettre cette facture',
        BUSY: 'Quelqu’un d’autre modifie cette facture en ce moment ; réessayez',
        OVER_OUTSTANDING: 'C’est plus que ce qui reste dû',
        HAS_PAYMENTS: 'Des règlements y sont rattachés : il faut la rectifier, pas l’annuler',
        HAS_RECTIFICATIONS: 'Une facture rectificative a déjà été émise : annulez-la ou émettez-en une autre',
        IS_DRAFT: 'Un brouillon se supprime, il ne s’annule pas',
        ALREADY_CANCELLED: 'Cette facture est déjà annulée',
        NOT_ISSUED: 'Un brouillon n’a été envoyé à personne, donc personne n’a pu le payer',
        CANCELLED: 'Cette facture a été annulée',
        ALREADY_PAID: 'Cette facture est déjà soldée',
        NOTHING_TO_COLLECT: 'Il n’y a rien à encaisser sur cette facture',
        INVALID_AMOUNT: 'Un règlement doit être un montant supérieur à zéro',
        VERIFACTU_DISABLED: 'Veri*Factu n’est pas activé pour cette entreprise',
        NO_NUMBER: 'Une facture sans numéro ne peut pas être enregistrée',
        NO_ISSUER_TAX_ID: 'Le numéro fiscal de l’entreprise manque dans Réglages → Entreprise',
        NO_RECORD: 'Cette facture n’a pas d’enregistrement Veri*Factu',
        NOT_SUBMITTABLE: 'Cet enregistrement n’est pas en attente d’envoi',
        NO_CERTIFICATE: 'Aucun certificat numérique n’est installé sur ce serveur',
        BAD_INVOICE_TYPE: 'Une extension a renvoyé un type de facture que la réglementation ne définit pas',
        NUMBER_TAKEN: 'Une facture porte déjà ce numéro',
        ORIGIN_ALREADY_IMPORTED: 'Cet enregistrement de l’autre logiciel a déjà été importé sous un autre numéro',
        NUMBER_IN_SERIES: 'Ce numéro a la forme de la numérotation de cet ERP et la série ne l’a pas encore atteint : avancez-la dans Paramètres → Numérotation',
        FUTURE_DATE: 'Une facture importée a déjà été émise : sa date ne peut pas être future',
        TOTALS_MISMATCH: 'Les lignes ne totalisent pas ce que l’autre logiciel a imprimé',
      },
      // ── Veri*Factu ────────────────────────────────────────────────────────
      // Désactivé sur toute instance tant qu’un client ne l’active pas : sans
      // cela, rien de ceci n’est affiché. Voir le VERIFACTU.md du module.
      verifactu: {
        title: 'Veri*Factu',
        subtitle: 'Le registre de chaque facture émise et la réponse du fisc',
        panelTitle: 'Enregistrement Veri*Factu',
        legend: 'Veri*Factu',
        off: 'Veri*Factu n’est pas activé pour cette entreprise.',
        notRegistered: 'Cette facture n’est pas dans la chaîne : elle est antérieure à l’activation.',
        qrHint: 'En scannant ce code, le client vérifie la facture auprès du fisc.',
        fingerprint: 'Empreinte',
        previousFingerprint: 'Empreinte précédente',
        position: 'Position dans la chaîne',
        generatedAt: 'Enregistré le',
        csv: 'Accusé du fisc (CSV)',
        attempts: '{count} tentatives',
        nextAttempt: 'Prochaine tentative : {date}',
        retry: 'Renvoyer',
        retryDone: 'Renvoyé',
        first: 'Premier enregistrement de la chaîne',
        type: { alta: 'Émission', anulacion: 'Annulation' },
        status: {
          pending: 'En attente d’envoi',
          sent: 'Envoyé',
          accepted: 'Accepté',
          accepted_with_errors: 'Accepté avec réserves',
          rejected: 'Rejeté',
          error: 'N’a pas pu être envoyé',
          not_sent: 'Enregistré, non envoyé',
        },
        mode: {
          verifactu: 'Chaque enregistrement est envoyé au fisc',
          sif: 'Le registre est conservé sans être envoyé',
        },
        environment: { test: 'Environnement de test', production: 'Environnement réel' },
        chain: {
          title: 'La chaîne',
          length: 'Enregistrements',
          last: 'Dernier enregistrement',
          lastHash: 'Dernière empreinte',
          empty: 'Aucune facture n’a encore été enregistrée.',
        },
        certificate: {
          title: 'Certificat numérique',
          ok: 'Installé',
          missing: 'Aucun n’est installé sur ce serveur',
          expires: 'Expire le {date}',
          expiringSoon: 'Expire dans {days} jours',
          expired: 'Expiré',
          unknown: 'La date d’expiration ne peut pas être lue',
          reasons: {
            NOT_CONFIGURED: 'Personne n’a indiqué où se trouve le certificat',
            FILE_NOT_FOUND: 'Le chemin configuré n’existe pas',
            NOT_READABLE: 'Le serveur ne peut pas lire le fichier',
          },
        },
        queue: {
          title: 'En attente d’arriver au fisc',
          empty: 'Il ne reste rien à envoyer.',
          stuck: 'Des envois sont bloqués depuis {hours} heures.',
        },
        verify: {
          title: 'Vérifier la chaîne',
          explain: 'Recalcule chaque empreinte et vérifie que chaque enregistrement suit le précédent.',
          run: 'Vérifier',
          intact: '{count} enregistrements vérifiés : la chaîne est intacte.',
          broken: '{count} enregistrements vérifiés : {problems} problèmes.',
          problems: {
            HASH_MISMATCH: 'L’empreinte ne correspond pas à l’enregistrement',
            BROKEN_LINK: 'Ne suit pas l’enregistrement précédent',
            GAP: 'Un enregistrement manque entre celui-ci et le précédent',
          },
        },
        ledger: {
          title: 'Le registre des factures',
          explain: 'Ce qui est remis lors d’un contrôle, dans l’ordre où il a été écrit.',
          columns: { position: '#', type: 'Type', number: 'Facture', date: 'Date', total: 'Total', status: 'État', fingerprint: 'Empreinte' },
          empty: 'Il n’y a pas encore d’enregistrements.',
        },
      },

      notifications: {
        categories: { verifactu: 'Veri*Factu : envois qui n’arrivent pas au fisc' },
        verifactu: {
          title: 'Veri*Factu demande votre attention',
          body: '{stuck} enregistrements non envoyés et {rejected} rejetés.',
        },
      },

      search: { draft: 'Brouillon sans numéro', subtitle: 'Facture' },
    },

    modules: { invoicing: 'Factures' },
    assistant: {
      openers: {
        invoicing: {
          'what-are-we-owed': 'Combien nous doit-on en ce moment ?',
          'overdue-invoices': 'Quelles factures sont échues ?',
          'invoiced-this-month': 'Combien avons-nous facturé ce mois-ci ?',
        },
      },
    },
    permissions: {
      invoicing: {
        read: 'Voir les factures et ce qui reste dû',
        write: 'Écrire et modifier des brouillons de facture',
        issue: 'Émettre des factures et les envoyer au client',
        collect: 'Enregistrer les règlements au fur et à mesure',
        manage: 'Annuler et rectifier des factures',
        import: 'Importer des factures émises avec un autre logiciel (migrations)',
      },
    },
    settings: {
      ns: {
        invoicing: {
          title: 'Factures',
          subtitle: 'Avec quelles données naît une facture, et ce qui arrive seul depuis d’autres modules',
          defaultTaxRate: { label: 'Taux de TVA d’une nouvelle ligne' },
          taxRates: {
            label: 'Taux de TVA utilisés par cette entreprise',
            help: 'Ce sont ceux que propose le sélecteur de chaque ligne.',
          },
          defaultRetentionRate: {
            label: 'Retenue appliquée à une nouvelle facture',
            help: 'Laissez 0 sauf si l’entreprise facture avec une retenue. Elle est déduite de la base imposable.',
          },
          showRetention: {
            label: 'Proposer la retenue (IRPF) lors de la rédaction d’une facture',
            help: 'Activez-le si cette entreprise facture avec une retenue. Avec un taux renseigné ci-dessus, la ligne apparaît de toute façon.',
          },
          defaultPaymentDays: { label: 'Jours entre la date de facture et l’échéance' },
          defaultPaymentTerms: { label: 'Conditions de paiement imprimées sur une nouvelle facture' },
          defaultPaymentMethod: { label: 'Moyen de paiement imprimé sur une nouvelle facture' },
          defaultCurrency: { label: 'Devise d’émission des factures', help: 'ISO 4217, trois lettres.' },
          draftFromQuote: {
            label: 'Ouvrir un brouillon de facture quand un devis est accepté',
            help: 'Un brouillon seulement : rien n’est émis ni envoyé tant que personne ne le dit.',
          },
          draftFromWorkOrder: { label: 'Ouvrir un brouillon de facture quand un ordre de travail est clôturé' },
          defaultHourlyRate: {
            label: 'À combien l’heure est facturée',
            help: 'Les ordres de travail notent des heures, jamais de l’argent. C’est le prix auquel elles entrent, et à 0 la ligne arrive pour que quelqu’un la chiffre.',
          },
          materialMarkup: {
            label: 'Marge sur le matériel venant d’un ordre de travail',
            help: '1 correspond au prix coûtant, 1,3 ajoute trente pour cent.',
          },
          labourLineLabel: { label: 'Nom de la ligne des heures sur la facture' },
          verifactu: {
            label: 'Tenir le registre Veri*Factu de chaque facture émise',
            help: 'Espagne, RD 1007/2023. Une fois activé, chaque facture est chaînée à la précédente et aucune ne peut être modifiée. Lisez le guide du module avant : le désactiver ensuite ne l’annule pas.',
          },
          verifactuSubmit: {
            label: 'Envoyer chaque enregistrement au fisc au fil de l’écriture',
            help: 'Désactivé, le registre est conservé sans être envoyé : c’est l’autre façon légale de se conformer, celle qui comporte le plus d’obligations. Dans les deux cas, rien ne quitte ce serveur tant qu’un certificat numérique n’y est pas installé.',
          },
        },
      },
    },
    audit: {
      actions: {
        invoicing: {
          draft_created: 'Brouillon de facture créé',
          draft_updated: 'Brouillon de facture modifié',
          draft_deleted: 'Brouillon de facture supprimé',
          invoice_issued: 'Facture émise',
          invoice_cancelled: 'Facture annulée',
          rectification_opened: 'Rectificative ouverte',
          payment_registered: 'Règlement enregistré',
          payment_removed: 'Règlement retiré',
          invoice_sent: 'Facture envoyée au client',
          verifactu_registered: 'Facture enregistrée dans Veri*Factu',
          verifactu_cancelled: 'Annulation enregistrée dans Veri*Factu',
          verifactu_resent: 'Enregistrement Veri*Factu renvoyé',
          invoice_imported: 'Facture importée d’un autre logiciel',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'
// Lo que dicen las tarjetas del asistente (backend/assistant.js): otro contrato,
// en su propio fichero por la misma razón.
import { withAssistantTools } from './i18n/assistant.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAssistantTools(withAnalytics(baseMessages, 'invoicing'), 'invoicing')))
